import React, { useState } from "react";
import { Link as GatsbyLink } from "gatsby";
import { IconFeatureFlag, Paragraph, TextContainer, Ul } from "@siteimprove/fancylib";
import {
	// TODO Add Knobs again
	// Knobs,
	Example,
	DocPageMeta,
	InlineMessage,
	ContentSection,
	HeaderSection,
	ImportExample,
	Header,
	Code,
} from "../../../../../src/docs";
import { LabWarning } from "../../../../../src/docs/docs-lab-warning";
import { NavigationItem, SideNavigation, ViewState } from "./side-navigation";

export const Meta: DocPageMeta = {
	category: "Navigation", // Which category does the component belong to?,
	title: "Side navigation", // Should the name in the styleguide menu be different from the component name?,
	notepad: "https://hackmd.io/xHKW4tbtTiqWpz2SGtP5aA", //Go to https://hackmd.io/ , create an account, and create a new note. Provide the url to the note here.
};

export default (): JSX.Element => (
	<>
		<HeaderSection
			title="Side navigation"
			subTitle="Allow users to explore content and features through a primary navigation structure with a clear visual hierarchy"
		/>
		<ContentSection>
			<TextContainer article>
				<LabWarning />
				<ImportExample lab component={SideNavigation} />
				<Header.H2>Examples</Header.H2>
				<Paragraph>
					Ideal for applications with multiple sections and subsections, where a clear visual
					hierarchy is essential.
				</Paragraph>
				<Paragraph>The side navigation consists of:</Paragraph>
				<Ul
					items={[
						<>
							<b>Links</b>: Clickable text or icons leading to different content areas.
						</>,
						<>
							<b>Icons</b>: Visual representations of navigation items (especially important in the
							collapsed view).
						</>,
						<>
							<b>Chevrons</b>: Expandable/collapsible indicators for nested navigation levels.
						</>,
						<>
							<b>Levels</b>: Primary (top-level), secondary, and tertiary levels to organize
							content.
						</>,
						<>
							<b>Collapsed view</b>: Can be used to collapse the navigation in mobile or tablet
							views.
						</>,
					]}
				/>
				<Header.H3>Basic usage</Header.H3>

				<Paragraph>
					This is a basic example, where we have selected an item under "data privacy" and the rest
					of the settings are at the default values.
				</Paragraph>
				<Paragraph>
					<b>Best practices</b>:
				</Paragraph>
				<Ul
					items={[
						"The selected item should be visually distinct.",
						"Navigation structure should mirror the content's logical hierarchy.",
						"Ensure the collapsed view uses clear, recognizable icons for each top-level item.",
					]}
				/>
				<Example fn={BasicUsage} />
				<Header.H3>Falling back to default expansion</Header.H3>
				<Paragraph>
					This example shows how the component falls back to showing expanded nodes that match with
					the selection if the expansion sent into the props are not properly showing the selected
					item. In this case, we select an item under "data privacy", but the expansion is set to
					showing something under QA. In this case, it will fall back to expanding the the nodes to
					show the selected item.
				</Paragraph>
				<Ul
					items={[
						<>
							<b>Use Case</b>: This behavior is important when the provided expansion state doesn't
							align with the user's selection. It ensures the user can easily see the context of
							their current selection.
						</>,
						<>
							<b>Best Practice</b>: Clearly indicate the selected item, even when the navigation
							falls back to a default state. This includes a subtle animation to smoothly transition
							between expansion states.
						</>,
					]}
				/>
				<Example fn={FallbackToDefaultExpansion} />
				<Header.H3>Using labels</Header.H3>
				<Paragraph>
					Menu-items can have labels. They are rendered using the <Code>Badge</Code> component. The
					label can have an optional icon, text, and tooltip.
				</Paragraph>
				<Ul
					items={[
						<>
							<b>Use Case</b>: To highlight new features, internal use or other contextual
							information.
						</>,
						<>
							<b>Best Practice</b>:
							<Ul
								items={[
									<>
										Keep labels concise and visually distinct (use the{" "}
										<GatsbyLink to="/lib/components/Visuals/Badge">Badge</GatsbyLink> component).
									</>,
									"If using icons, choose universally recognizable ones.",
								]}
							/>
							.
						</>,
					]}
				/>

				<Example fn={LabelUsage} />
				<Header.H3>Links with onClick handlers</Header.H3>
				<Paragraph>
					This example has items with both href's but also onclick handlers. The idea is that simple
					clicks will be handled with the "onClick" handler and "preventDefault" so that the browser
					doesn't navigate. But if you "open in new tab" click, then the navigation happens.
				</Paragraph>
				<Ul
					items={[
						<>
							<b>Use Case</b>: This pattern is useful when you need to trigger specific actions in
							addition to, or instead of, navigating to a new page.
						</>,
						<>
							<b>Best Practice</b>:
							<Ul
								items={[
									"Clearly communicate to the user whether a click will result in navigation or a different action.",

									"If the custom action fails, provide a fallback to ensure the user can still navigate to the destination.",
								]}
							/>
						</>,
					]}
				/>
				<Example fn={LinksWithClickUsage} />
				<Header.H3>One main nav item and fixed view</Header.H3>
				<Paragraph>
					If there is only one main nav item in <Code>mainItems</Code> the sub nav will be shown on
					load and the "Back to main navigation" button will be replaced by a back link that uses
					the props <Code>backLink</Code> and <Code>backLinkLabel</Code>. This can be combined with{" "}
					<Code>viewState="fixed"</Code> which will hide the view state toggle button to collapse
					and expand the navigation.
				</Paragraph>
				<Ul
					items={[
						<>
							<b>Use Case</b>: This is perfect for applications with a single primary section and a
							fixed secondary navigation structure (E.g Page Inspector)
						</>,
						<>
							<b>Best Practice</b>: In this scenario, ensure the back link is clearly visible and
							labeled. Since the navigation is always visible, make sure it doesn't obscure or
							overlap with the main content area.
						</>,
					]}
				/>

				<Example fn={OneMainNavItemFixedViewUsage} />
				<Header.H2>Properties</Header.H2>
				{/* TODO Add Knobs again */}
				{/* <Knobs component={SideNavigation} initialProps={{ value: "Some value" }} /> */}
				<Header.H2>Guidelines</Header.H2>
				<Header.H3>Best practices</Header.H3>
				<InlineMessage variant="best-practices">
					<Header.H4>General</Header.H4>
					<Paragraph>
						Use <Code>SideNavigation</Code> for deep hierarchies or when users frequently switch
						sections.
					</Paragraph>
					<Header.H4>Placement</Header.H4>
					<Paragraph>
						Place <Code>SideNavigation</Code> on the left (desktop). For smaller screen (e.g
						mobile), use collapsed view or{" "}
						<GatsbyLink to="/lab/components/Navigation/Horizontal navigation">
							Horizontal navigation
						</GatsbyLink>
						.
					</Paragraph>
					<Header.H4>Style</Header.H4>
					<Ul
						items={[
							<>
								<b>Siteimprove Design System</b>: Adhere to Siteimprove's guidelines for color,
								typography, and spacing. If you are not using a component from Fancy, match the
								styling of your <Code>SideNavigatio</Code> to existing components for visual
								consistency.
							</>,
							"Limit navigation depth to three levels maximum.",
							"Prioritize the most important navigation items at the top.",
							"Match page titles to navigation link labels.",
						]}
					/>
					<Header.H4>Interaction</Header.H4>
					<Ul
						items={[
							"Use chevrons for expanding/collapsing nested levels.",
							"Keep quaternary levels (4th level) as simple page links.",
							"If more than five secondary items, consider using sub-menus.",
						]}
					/>
					<Header.H4>Responsive layout</Header.H4>
					<Ul
						items={[
							"Default to the expanded view.",
							"Provide a collapse option with clear icons.",
							"Auto-collapse for smaller screens (e.g. breakpoints of 768 pixels for tablet and mobile).",
						]}
					/>
				</InlineMessage>
				<Header.H3>Do not use when</Header.H3>
				<InlineMessage variant="do-not-use-when">
					<Ul
						items={[
							"Navigation is very simple (few top-level items).",
							<>
								<GatsbyLink to="/lab/components/Navigation/Horizontal navigation">
									Horizontal navigation
								</GatsbyLink>{" "}
								is more suitable (e.g., system-level or shortcuts).
							</>,
						]}
					/>
				</InlineMessage>
				<Header.H3>Accessibility</Header.H3>
				<InlineMessage variant="accessibility">
					<Header.H4>For designers</Header.H4>
					<Ul items={["Ensure icon meanings are clear and labels are intuitive."]} />
					<Header.H4>For developers</Header.H4>
					<Ul
						items={[
							<>
								Navigation link labels should be readable by assistive technologies, even when
								navigation is collapsed
							</>,
						]}
					/>
				</InlineMessage>
				<Paragraph>
					Explore detailed guidelines for this component:{" "}
					<a href="https://siteimprove-wgs.atlassian.net/wiki/x/4wQNeQ">
						Accessibility Specifications
					</a>
				</Paragraph>
				<Header.H3>Writing</Header.H3>
				<InlineMessage variant="writing">
					<Ul
						items={[
							"Use clear, concise labels under 20 characters to prevent text wrapping. Labels should be scannable yet descriptive.",
							"Keep labels consistent in length, style (nouns or verbs), and sentence case.",
						]}
					/>
				</InlineMessage>
			</TextContainer>
		</ContentSection>
	</>
);

const data = {
	personFullName: "Siteimprove User",
	languageCulture: "en-US",
	formatCulture: "en-US",
	myProfile: {
		id: "my-profile",
		title: "My profile",
		href: "/Settings/Users/MyProfile",
		icon: null,
		observeKey: "top-nav-my-profile",
	},
	horizontalNavigation: [
		{
			id: "demo-data",
			title: "Configure Demo Data",
			href: "/App/State/ConfigureDemoData/1",
			icon: null,
			observeKey: "top-nav-demo-data",
		},
		{
			id: "signout",
			title: "Sign out",
			href: "/Auth/Logout",
			icon: null,
			observeKey: "top-nav-signout",
		},
	],
	sideNavigation: [
		{
			children: [],
			defaultChildId: null,
			id: "dashboard",
			title: "Dashboard",
			href: "/Dashboard/0/20063/Custom/Dashboard/Index?groupId=-1",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M8.94204 10.6454H3.72293C2.80659 10.6454 2.04962 9.88841 2.04962 8.97207V3.75296C2.04962 2.83662 2.80659 2.07965 3.72293 2.07965H8.92212C9.85838 2.07965 10.6153 2.83662 10.6153 3.75296V8.95215C10.6153 9.88841 9.85838 10.6454 8.94204 10.6454Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M8.94204 22H3.72293C2.80659 22 2.04962 21.243 2.04962 20.3267V15.1076C2.04962 14.1912 2.80659 13.4343 3.72293 13.4343H8.92212C9.85838 13.4343 10.5954 14.1912 10.5954 15.1076V20.3068C10.6154 21.243 9.85838 22 8.94204 22Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.2966 13.4343H15.0775C14.1412 13.4343 13.4042 14.1912 13.4042 15.1076V20.3068C13.4042 21.243 14.1612 21.9801 15.0775 21.9801H20.2767C21.2129 21.9801 21.95 21.2231 21.95 20.3068V15.1076C21.9699 14.1912 21.2129 13.4343 20.2966 13.4343Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.2966 2H15.0775C14.1612 2 13.4042 2.73705 13.4042 3.67331V8.8725C13.4042 9.80876 14.1612 10.5458 15.0775 10.5458H20.2767C21.2129 10.5458 21.95 9.78884 21.95 8.8725V3.67331C21.9699 2.73705 21.2129 2 20.2966 2Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-dashboard",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "content-insights",
					title: "Core Wins",
					href: "/Insights/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-content-insights",
				},
			],
			defaultChildId: "content-insights",
			id: "content-insights-area",
			title: "Core Wins",
			href: "/Insights/20063/Overview/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.96 15.44C13.8598 15.44 15.4 13.8999 15.4 12C15.4 10.1001 13.8598 8.56 11.96 8.56C10.0601 8.56 8.51999 10.1001 8.51999 12C8.51999 13.8999 10.0601 15.44 11.96 15.44Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M19.8 6.18L13.08 2.3C12.38 1.9 11.54 1.9 10.84 2.3L4.12 6.18C3.42 6.58 3 7.32 3 8.12V15.88C3 16.68 3.42 17.42 4.12 17.82L10.84 21.7C11.54 22.1 12.38 22.1 13.08 21.7L19.8 17.82C20.5 17.42 20.92 16.68 20.92 15.88V8.12C20.92 7.32 20.5 6.58 19.8 6.18ZM11.96 17.04C9.18 17.04 6.92 14.78 6.92 12C6.92 9.22 9.18 6.96 11.96 6.96C14.74 6.96 17 9.22 17 12C17 14.78 14.74 17.04 11.96 17.04Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-content-insights-area",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "dci-overview",
					title: "DCI overview",
					href: "/Dci/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-dci-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dci-account-overview",
					title: "My sites",
					href: "/Dci/20063/Overview/SitesOverview",
					icon: null,
					observeKey: "side-nav-dci-account-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dci-site-benchmark",
					title: "DCI™ Site Benchmark",
					href: "/Dci/20063/SiteBenchmark/Index",
					icon: null,
					observeKey: "side-nav-dci-site-benchmark",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dci-industry-benchmark",
					title: "Industry Benchmark Explorer",
					href: "/Dci/20063/IndustryBenchmark/Index",
					icon: null,
					observeKey: "side-nav-dci-industry-benchmark",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dci-management",
					title: "Management",
					href: "/Dci/20063/Management/Index",
					icon: null,
					observeKey: "side-nav-dci-management",
				},
			],
			defaultChildId: "dci-overview",
			id: "dci",
			title: "Digital Certainty Index",
			href: "/Dci/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.7357 21.3761C6.56402 21.3761 2.35959 17.1716 2.35959 12C2.35959 6.82834 6.56402 2.6239 11.7357 2.6239C16.9073 2.6239 21.1117 6.82834 21.1117 12C21.1117 17.1716 16.9073 21.3761 11.7357 21.3761ZM11.7357 3.61086C7.11671 3.61086 3.34654 7.38103 3.34654 12C3.34654 16.6189 7.11671 20.3891 11.7357 20.3891C16.3546 20.3891 20.1248 16.6189 20.1248 12C20.1248 7.38103 16.3546 3.61086 11.7357 3.61086Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M11.7357 22.2643C7.47202 22.2643 3.58342 19.5601 2.08324 15.5333C1.82664 14.8227 2.18194 14.0134 2.89255 13.7568C3.60316 13.4804 4.41246 13.8555 4.66907 14.5661C5.77446 17.527 8.61689 19.5009 11.7357 19.5009C15.8809 19.5009 19.2365 16.1452 19.2365 12C19.2365 7.85479 15.8809 4.49913 11.7357 4.49913C10.9658 4.49913 10.3539 3.88722 10.3539 3.1174C10.3539 2.34757 10.9658 1.73566 11.7357 1.73566C17.4008 1.73566 22 6.33487 22 12C22 17.6651 17.4008 22.2643 11.7357 22.2643Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M10.3934 12C10.3934 13.0659 9.60383 13.8752 8.5774 13.8752H7.03775V10.1248H8.5774C9.60383 10.1248 10.3934 10.9341 10.3934 12ZM9.44592 12C9.44592 11.4275 9.07088 11.0525 8.55766 11.0525H8.02471V12.9475H8.55766C9.09062 12.9475 9.44592 12.5724 9.44592 12Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M10.9066 12C10.9066 10.8946 11.7357 10.0458 12.8608 10.0458C13.5517 10.0458 14.1636 10.4011 14.4794 10.934L13.6701 11.4078C13.5319 11.1314 13.2161 10.9735 12.8805 10.9735C12.2686 10.9735 11.8738 11.388 11.8738 12C11.8738 12.5921 12.2686 13.0264 12.8805 13.0264C13.2358 13.0264 13.5319 12.8685 13.6701 12.5921L14.4991 13.0659C14.1636 13.5988 13.5714 13.9541 12.8805 13.9541C11.7357 13.9541 10.9066 13.1053 10.9066 12Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M16.019 10.1248V13.8752H15.0518V10.1248H16.019Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-dci",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "a11y-overview-nextgen-primary",
					title: "Accessibility overview",
					href: "/Accessibility/20063/NextGen/Overview",
					icon: null,
					observeKey: "side-nav-a11y-overview-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-site-target-nextgen-primary",
					title: "Site target",
					href: "/Accessibility/20063/NextGen/SiteTarget",
					icon: null,
					observeKey: "side-nav-a11y-site-target-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-my-sites-nextgen-primary",
					title: "My sites",
					href: "/Accessibility/20063/NextGen/Sites",
					icon: null,
					observeKey: "side-nav-a11y-my-sites-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-groups-nextgen-primary",
					title: "Groups",
					href: "/Accessibility/20063/NextGen/Groups",
					icon: null,
					observeKey: "side-nav-a11y-groups-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-issues-nextgen-primary",
					title: "Issues",
					href: "/Accessibility/20063/NextGen/IssuesOverview?IssueKind=Confirmed",
					icon: null,
					observeKey: "side-nav-a11y-issues-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-potential-issues-nextgen-primary",
					title: "Potential issues",
					href: "/Accessibility/20063/NextGen/IssuesOverview?IssueKind=Potential",
					icon: null,
					observeKey: "side-nav-a11y-potential-issues-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-passed-issues-nextgen-primary",
					title: "Resolved issues",
					href: "/Accessibility/20063/NextGen/IssuesOverview?IssueKind=Passed",
					icon: null,
					observeKey: "side-nav-a11y-passed-issues-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-activity-nextgen-primary",
					title: "Activity",
					href: "/Accessibility/20063/NextGen/Activity",
					icon: null,
					observeKey: "side-nav-a11y-activity-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-guidelines-nextgen-primary",
					title: "Guidelines",
					href: "/Accessibility/20063/NextGen/Guidelines",
					icon: null,
					observeKey: "side-nav-a11y-guidelines-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-pdfs-nextgen-primary",
					title: "PDFs",
					href: "/Accessibility/20063/Documents/Index?LoadPersonalFilters=True",
					icon: null,
					observeKey: "side-nav-a11y-pdfs-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-nextgen-policies-nextgen-primary",
					title: "Accessibility policies",
					href: "/Accessibility/20063/Overview/Policies",
					icon: null,
					observeKey: "side-nav-a11y-nextgen-policies-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-nextgen-fancy-single-page-check-nextgen-primary",
					title: "Single page check",
					href: "/Accessibility/20063/NextGen/SinglePageCheck",
					icon: null,
					observeKey: "side-nav-a11y-nextgen-fancy-single-page-check-nextgen-primary",
				},
				{
					children: [],
					defaultChildId: null,
					id: "a11y-browser-extension",
					title: "Browser extension",
					href: "/Accessibility/20063/NextGen/AlfaBrowserExtension",
					icon: null,
					observeKey: "side-nav-a11y-browser-extension",
				},
				{
					children: [],
					defaultChildId: null,
					id: "accessibility-issue-definitions",
					title: "Issue Definitions (debug only)",
					href: "/Accessibility/Issues/Definitions",
					icon: null,
					observeKey: "side-nav-accessibility-issue-definitions",
				},
			],
			defaultChildId: "a11y-overview-nextgen-primary",
			id: "accessibility",
			title: "Accessibility",
			href: "/Accessibility/20063/Overview/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.7742 6.68579C12.7886 6.05876 13.1027 4.72807 12.4757 3.71362C11.8487 2.69917 10.518 2.3851 9.50352 3.01213C8.48906 3.63915 8.17499 4.96984 8.80202 5.98429C9.42905 6.99875 10.7597 7.31282 11.7742 6.68579Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M17.6595 12.8449C15.0789 12.8449 13.4119 15.5105 13.3822 15.558C13.3407 15.6094 13.3189 15.6727 13.3189 15.738C13.3189 15.7933 13.3348 15.8487 13.3664 15.8962C13.3723 15.906 14.7704 18.631 17.6595 18.631C20.5485 18.631 21.9327 15.9337 21.9486 15.9001C21.9486 15.9001 21.9505 15.8962 21.9525 15.8942C21.9822 15.8467 22 15.7933 22 15.736C22 15.6707 21.9782 15.6074 21.9387 15.556C21.909 15.5066 20.242 12.843 17.6614 12.843L17.6595 12.8449ZM17.6595 13.713C18.7787 13.713 19.6844 14.6207 19.6844 15.738C19.6844 16.8552 18.7767 17.7629 17.6595 17.7629C16.5422 17.7629 15.6345 16.8552 15.6345 15.738C15.6345 14.6207 16.5422 13.713 17.6595 13.713Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M17.6595 16.606C18.139 16.606 18.5276 16.2173 18.5276 15.7379C18.5276 15.2584 18.139 14.8698 17.6595 14.8698C17.1801 14.8698 16.7914 15.2584 16.7914 15.7379C16.7914 16.2173 17.1801 16.606 17.6595 16.606Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M12.5141 16.3648C12.4054 16.1749 12.348 15.9594 12.348 15.7379C12.348 15.4769 12.4311 15.2218 12.5814 15.0082C12.6901 14.8382 13.143 14.1757 13.8786 13.5014V12.4059C13.8786 11.8107 14.3631 11.3262 14.9583 11.3262H18.1973C18.7945 11.3262 19.277 10.8417 19.277 10.2465C19.277 9.65129 18.7945 9.16681 18.1973 9.16681H3.07969C2.4825 9.16681 2 9.65129 2 10.2465C2 10.8417 2.4825 11.3262 3.07969 11.3262H6.31877C6.91398 11.3262 7.39846 11.8107 7.39846 12.4059V21.0078C7.39846 21.6228 7.89875 22.1231 8.51374 22.1231C9.12873 22.1231 9.5875 21.6683 9.62705 21.081L10.0997 16.1472C10.1194 15.8645 10.3528 15.645 10.6375 15.645C10.9223 15.645 11.1556 15.8645 11.1754 16.1472L11.648 21.081C11.6876 21.6663 12.174 22.1211 12.7613 22.1211C13.3486 22.1211 13.8766 21.6208 13.8766 21.0058V18.0733C13.1825 17.4464 12.7237 16.7741 12.5141 16.3608V16.3648Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-accessibility",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "qa-dci",
					title: "QA overview",
					href: "/QualityAssurance/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-qa-dci",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-all-sites",
							title: "My sites",
							href: "/QualityAssurance/20063/Overview/AllSites",
							icon: null,
							observeKey: "side-nav-qa-summary-all-sites",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-groups",
							title: "Groups",
							href: "/QualityAssurance/20063/Overview/Groups",
							icon: null,
							observeKey: "side-nav-qa-summary-groups",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-policies",
							title: "QA policies",
							href: "/QualityAssurance/20063/Overview/Policies",
							icon: null,
							observeKey: "side-nav-qa-summary-policies",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-priority-pages",
							title: "Priority pages",
							href: "/QualityAssurance/20063/Overview/PriorityPages",
							icon: null,
							observeKey: "side-nav-qa-summary-priority-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-single-page-check",
							title: "Single page check",
							href: "/QualityAssurance/20063/Overview/SinglePageCheck",
							icon: null,
							observeKey: "side-nav-qa-summary-single-page-check",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-summary-check-history",
							title: "Check history",
							href: "/QualityAssurance/20063/Overview/CheckHistory",
							icon: null,
							observeKey: "side-nav-qa-summary-check-history",
						},
					],
					defaultChildId: null,
					id: "qa-summary",
					title: "Summary",
					href: "/QualityAssurance/Home/Index",
					icon: null,
					observeKey: "side-nav-qa-summary",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-summary",
							title: "Summary",
							href: "/QualityAssurance/20063/Links/Index",
							icon: null,
							observeKey: "side-nav-qa-links-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-pages",
							title: "Pages with broken links",
							href: "/QualityAssurance/20063/Links/Pages",
							icon: null,
							observeKey: "side-nav-qa-links-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-broken-links",
							title: "Broken links",
							href: "/QualityAssurance/20063/Links/BrokenLinks",
							icon: null,
							observeKey: "side-nav-qa-links-broken-links",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-unsafe-domains",
							title: "Links to unsafe domains",
							href: "/QualityAssurance/20063/Links/UnsafeDomains",
							icon: null,
							observeKey: "side-nav-qa-links-unsafe-domains",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-documents",
							title: "PDFs with broken links",
							href: "/QualityAssurance/20063/Links/Documents",
							icon: null,
							observeKey: "side-nav-qa-links-documents",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-links-broken-document-links",
							title: "Broken links in PDFs",
							href: "/QualityAssurance/20063/Links/BrokenDocumentLinks",
							icon: null,
							observeKey: "side-nav-qa-links-broken-document-links",
						},
					],
					defaultChildId: null,
					id: "qa-links",
					title: "Links",
					href: "/QualityAssurance/Home/Index",
					icon: null,
					observeKey: "side-nav-qa-links",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-summary",
							title: "Find misspellings",
							href: "/QualityAssurance/20063/Spelling/IndexV2",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-pages-with-spelling-issues",
							title: "Pages with misspellings or words to review",
							href: "/QualityAssurance/20063/Spelling/PagesWithSpellingIssues",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-pages-with-spelling-issues",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-spelling-decisions",
							title: "Decisions on spellings",
							href: "/QualityAssurance/20063/Spelling/SpellingDecisions",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-spelling-decisions",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-word-inventory",
							title: "Word inventory",
							href: "/QualityAssurance/20063/Spelling/WordInventory",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-word-inventory",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-add-words",
							title: "Add words",
							href: "/QualityAssurance/20063/Spelling/AddWords",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-add-words",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-spelling-v2-spelling-overview",
							title: "Progress and trends",
							href: "/QualityAssurance/20063/Spelling/SpellingOverview",
							icon: null,
							observeKey: "side-nav-qa-spelling-v2-spelling-overview",
						},
					],
					defaultChildId: null,
					id: "qa-spelling-v2",
					title: "Spelling",
					href: "/QualityAssurance/Home/Index",
					icon: null,
					observeKey: "side-nav-qa-spelling-v2",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "qa-readability-scores",
							title: "Readability test results",
							href: "/QualityAssurance/20063/Readability/Scores",
							icon: null,
							observeKey: "side-nav-qa-readability-scores",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-readability-pages",
							title: "Pages",
							href: "/QualityAssurance/20063/Readability/Pages",
							icon: null,
							observeKey: "side-nav-qa-readability-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-readability-decisions",
							title: "Excluded pages",
							href: "/QualityAssurance/20063/Readability/Decisions",
							icon: null,
							observeKey: "side-nav-qa-readability-decisions",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-readability-accepted-words",
							title: "Accepted words",
							href: "/QualityAssurance/20063/Readability/AcceptedWords",
							icon: null,
							observeKey: "side-nav-qa-readability-accepted-words",
						},
					],
					defaultChildId: null,
					id: "qa-readability",
					title: "Readability",
					href: "/QualityAssurance/Home/Index",
					icon: null,
					observeKey: "side-nav-qa-readability",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-summary",
							title: "Summary",
							href: "/QualityAssurance/20063/Inventory/Index",
							icon: null,
							observeKey: "side-nav-qa-inventory-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-pages",
							title: "Pages",
							href: "/QualityAssurance/20063/Inventory/Pages",
							icon: null,
							observeKey: "side-nav-qa-inventory-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-links",
							title: "Links",
							href: "/QualityAssurance/20063/Inventory/Links",
							icon: null,
							observeKey: "side-nav-qa-inventory-links",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-documents",
							title: "Documents",
							href: "/QualityAssurance/20063/Inventory/Documents",
							icon: null,
							observeKey: "side-nav-qa-inventory-documents",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-media",
							title: "Media files",
							href: "/QualityAssurance/20063/Inventory/Media",
							icon: null,
							observeKey: "side-nav-qa-inventory-media",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-java-script",
							title: "JavaScript files",
							href: "/QualityAssurance/20063/Inventory/JavaScript",
							icon: null,
							observeKey: "side-nav-qa-inventory-java-script",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-css",
							title: "CSS",
							href: "/QualityAssurance/20063/Inventory/Css",
							icon: null,
							observeKey: "side-nav-qa-inventory-css",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-link-text",
							title: "Link text",
							href: "/QualityAssurance/20063/Inventory/LinkText",
							icon: null,
							observeKey: "side-nav-qa-inventory-link-text",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-email-addresses",
							title: "Email addresses",
							href: "/QualityAssurance/20063/Inventory/EmailAddresses",
							icon: null,
							observeKey: "side-nav-qa-inventory-email-addresses",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-phone-numbers",
							title: "Phone numbers",
							href: "/QualityAssurance/20063/Inventory/PhoneNumbers",
							icon: null,
							observeKey: "side-nav-qa-inventory-phone-numbers",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-personal-id-numbers",
							title: "CPR-numre",
							href: "/QualityAssurance/20063/Inventory/PersonalIdNumbers",
							icon: null,
							observeKey: "side-nav-qa-inventory-personal-id-numbers",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-meta-tags",
							title: "Meta tags",
							href: "/QualityAssurance/20063/Inventory/MetaTags",
							icon: null,
							observeKey: "side-nav-qa-inventory-meta-tags",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-site-map",
							title: "Sitemap",
							href: "/QualityAssurance/20063/Inventory/SiteMap",
							icon: null,
							observeKey: "side-nav-qa-inventory-site-map",
						},
						{
							children: [],
							defaultChildId: null,
							id: "qa-inventory-body-tags",
							title: "Capture tags",
							href: "/QualityAssurance/20063/Inventory/BodyTags",
							icon: null,
							observeKey: "side-nav-qa-inventory-body-tags",
						},
					],
					defaultChildId: null,
					id: "qa-inventory",
					title: "Inventory",
					href: "/QualityAssurance/Home/Index",
					icon: null,
					observeKey: "side-nav-qa-inventory",
				},
			],
			defaultChildId: "qa-dci",
			id: "qa",
			title: "Quality Assurance",
			href: "/QualityAssurance/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M21.7707 7.85325L17.6138 3.22995C17.4516 3.04745 17.2082 2.92578 16.9649 2.92578H8.04274C7.79941 2.92578 7.55608 3.02717 7.39386 3.22995L3.23694 7.85325C2.95305 8.15741 2.93277 8.6238 3.17611 8.96852L5.04165 11.5032C5.32554 11.4627 5.60942 11.4221 5.91359 11.4221C8.65107 11.4221 10.8613 13.6324 10.8613 16.3699C10.8613 17.2012 10.6586 17.9718 10.2936 18.6612L11.7941 20.7093C12.1388 21.1959 12.8688 21.1959 13.2135 20.7093L21.8315 8.9888C22.0749 8.6238 22.0546 8.15741 21.7707 7.85325ZM9.6244 8.84685C9.19857 8.96852 8.87412 9.29296 8.75246 9.71879L8.30635 11.4221L7.86024 9.71879C7.75885 9.29296 7.41413 8.96852 6.9883 8.84685L5.28498 8.40074L6.9883 7.95464C7.41413 7.85325 7.73858 7.50853 7.86024 7.0827L8.30635 5.37938L8.75246 7.0827C8.87412 7.50853 9.19857 7.83297 9.6244 7.95464L11.3277 8.40074L9.6244 8.84685ZM14.8155 4.20327H16.316C16.3769 4.20327 16.458 4.22355 16.4985 4.28438L19.2766 7.34631C19.4185 7.50853 19.3171 7.75186 19.0941 7.75186H16.5594C16.4782 7.75186 16.3971 7.71131 16.3566 7.63019L14.6127 4.56827C14.5316 4.40605 14.633 4.20327 14.8155 4.20327ZM19.2968 9.59713L15.0385 15.4168C14.8966 15.6196 14.5924 15.4574 14.6533 15.2343L16.2957 9.41463C16.316 9.31324 16.4174 9.25241 16.4985 9.25241H19.1143C19.2968 9.25241 19.3982 9.45518 19.2968 9.59713Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M5.93386 12.3954C8.10357 12.3954 9.86773 14.1596 9.86773 16.3293C9.86773 18.499 8.10357 20.2632 5.93386 20.2632C3.76415 20.2632 2 18.499 2 16.3293C2 14.1596 3.76415 12.3954 5.93386 12.3954ZM5.40664 16.6335L4.51443 15.7413C4.41304 15.6399 4.2711 15.6399 4.16971 15.7413L3.80471 16.1063C3.70332 16.2076 3.70332 16.3496 3.80471 16.451L4.69693 17.3432L5.40664 18.0529L6.11636 17.3432L8.06301 15.3965C8.1644 15.2952 8.1644 15.1532 8.06301 15.0518L7.69802 14.6868C7.59663 14.5854 7.45469 14.5854 7.3533 14.6868L5.40664 16.6335Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-qa",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "policy-policy-overview",
					title: "Policy overview",
					href: "/Policy/73617/Overview/Index",
					icon: null,
					observeKey: "side-nav-policy-policy-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "policy-my-policies",
					title: "My policies",
					href: "/Policy/73617/ContentPolicies/Index",
					icon: null,
					observeKey: "side-nav-policy-my-policies",
				},
				{
					children: [],
					defaultChildId: null,
					id: "policy-policy-library",
					title: "Policy library",
					href: "/Policy/73617/Library/Index",
					icon: null,
					observeKey: "side-nav-policy-policy-library",
				},
				{
					children: [],
					defaultChildId: null,
					id: "policy-my-sites",
					title: "My sites",
					href: "/Policy/73617/Overview/Sites",
					icon: null,
					observeKey: "side-nav-policy-my-sites",
				},
			],
			defaultChildId: "policy-policy-overview",
			id: "policy",
			title: "Policy ",
			href: "/Policy/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.9516 4.43046L8.65428 8.67023C8.53727 8.82127 8.53515 9.02975 8.65215 9.18079C8.98614 9.61477 9.18186 10.0466 10.1902 10.889C8.94785 12.2824 2.71263 19.794 2.3595 20.2472C1.78086 20.9917 1.91488 22.066 2.65945 22.6447C3.40402 23.2233 4.47832 23.0893 5.05696 22.3447C5.41009 21.8916 11.1092 13.9652 12.1537 12.4165C13.0749 13.0781 13.5365 13.1972 14.0981 13.4546C14.3492 13.5695 14.647 13.4971 14.8172 13.278L17.9911 9.19781C18.1464 8.99784 18.155 8.71916 18.0082 8.51281C17.6465 8.00225 17.1253 7.23003 15.5766 6.0366C13.979 4.807 13.0834 4.49003 12.5218 4.27304C12.3175 4.19433 12.0857 4.25815 11.9516 4.43046Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M21.1033 9.45757L20.974 9.357C20.4788 8.97177 19.7651 9.06092 19.3799 9.55612L15.571 14.4524C15.1858 14.9476 15.2749 15.6613 15.7701 16.0465L15.8994 16.1471C16.3946 16.5323 17.1084 16.4432 17.4936 15.948L21.3025 11.0517C21.6877 10.5565 21.5985 9.8428 21.1033 9.45757Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M11.0101 1.79388L10.8808 1.6933C10.3856 1.30808 9.67187 1.39723 9.28664 1.89243L5.47775 6.78868C5.09253 7.28388 5.18168 7.9976 5.67688 8.38283L5.80617 8.48341C6.30137 8.86863 7.0151 8.77948 7.40032 8.28428L11.2092 3.38803C11.5944 2.89283 11.5053 2.1791 11.0101 1.79388Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.0674 22.968H12.3686C11.8517 22.968 11.4326 22.5489 11.4326 22.032C11.4326 21.515 11.8517 21.0959 12.3686 21.0959H20.0674C20.5844 21.0959 21.0035 21.515 21.0035 22.032C21.0035 22.5489 20.5844 22.968 20.0674 22.968Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-policy",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "performance-page-overview",
					title: "Performance overview",
					href: "/Performance/pages",
					icon: null,
					observeKey: "side-nav-performance-page-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "performance-profiles-page",
					title: "Performance profiles",
					href: "/Performance/profiles",
					icon: null,
					observeKey: "side-nav-performance-profiles-page",
				},
			],
			defaultChildId: "performance-page-overview",
			id: "performance",
			title: "Performance",
			href: "/Performance",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M15.6447 20.8288H8.3515C7.86179 20.8288 7.46478 20.4318 7.46478 19.9421C7.46478 19.4524 7.86179 19.0554 8.3515 19.0554H15.6447C16.1344 19.0554 16.5314 19.4524 16.5314 19.9421C16.5314 20.4318 16.1344 20.8288 15.6447 20.8288Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M12.6178 5.32947C12.6178 5.72245 12.6319 6.07512 12.6138 6.42577C12.5876 6.93966 12.0777 7.20971 11.6343 6.9578C11.3965 6.82278 11.324 6.60715 11.326 6.35121C11.326 6.02474 11.326 5.69826 11.326 5.33552C9.88106 5.45442 8.61951 5.93002 7.43857 6.74821C7.6542 7.00617 7.8517 7.23994 8.04919 7.47774C8.36156 7.85258 8.36156 8.173 8.05524 8.43095C7.73078 8.70503 7.32975 8.65666 7.0335 8.30399C6.84004 8.07627 6.64859 7.84653 6.43699 7.59664C5.41928 8.61434 4.75425 9.78521 4.37336 11.1636C4.71394 11.2261 5.01623 11.2825 5.31852 11.341C5.73769 11.4196 5.99564 11.7259 5.93519 12.0705C5.86264 12.4857 5.50795 12.7073 5.06056 12.6227C4.8006 12.5723 4.5366 12.534 4.27461 12.4917C4.24237 12.4857 4.20609 12.4998 4.14967 12.5058C4.0348 13.8984 4.26051 15.2204 4.87113 16.5263C5.16536 16.355 5.43742 16.1756 5.72762 16.0386C5.89488 15.96 6.09842 15.8995 6.27577 15.9197C6.55589 15.9499 6.68688 16.1857 6.73121 16.4436C6.77958 16.7278 6.68889 16.9757 6.43094 17.1308C5.99161 17.3968 5.54624 17.6548 5.10087 17.9148C4.82478 18.076 4.54869 18.2372 4.2726 18.3944C3.86753 18.6241 3.54307 18.5375 3.3093 18.1344C2.73093 17.1349 2.31578 16.0688 2.14448 14.9302C1.54595 10.9662 2.80549 7.6974 5.95131 5.21863C8.01292 3.59433 10.4151 2.92527 13.0188 3.25174C17.2146 3.77772 20.0642 6.09527 21.4547 10.0714C22.424 12.8444 22.0694 15.5589 20.6425 18.1264C20.4229 18.5214 20.0622 18.6241 19.6732 18.4004C18.9659 17.9934 18.2625 17.5802 17.5632 17.1611C17.1924 16.9394 17.1017 16.5565 17.3194 16.2018C17.5149 15.8854 17.8494 15.8229 18.2242 16.0345C18.5003 16.1897 18.7724 16.3509 19.0727 16.5263C19.6893 15.2365 19.913 13.9064 19.7982 12.4715C19.4737 12.526 19.1694 12.5743 18.8691 12.6308C18.4318 12.7114 18.1013 12.4998 18.0187 12.0806C17.9481 11.7259 18.196 11.4236 18.6172 11.347C18.9215 11.2906 19.2238 11.2342 19.5704 11.1697C19.1573 9.60384 18.3492 8.31407 17.1561 7.26614C15.8744 6.13961 14.3771 5.49875 12.6218 5.3335L12.6178 5.32947Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M13.6476 14.0797L16.5697 8.69698C16.6866 8.48336 16.6423 8.21533 16.4609 8.05209C16.2755 7.88684 16.0014 7.87072 15.7999 8.01582L11.0459 11.4156C10.8827 11.5123 10.7295 11.6372 10.5965 11.7864C9.89316 12.5764 9.96168 13.7875 10.7517 14.4908C11.5416 15.1942 12.7528 15.1257 13.4561 14.3357C13.5287 14.2551 13.5912 14.1664 13.6476 14.0777V14.0797Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-performance",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-overview",
					title: "SEO Overview",
					href: "/SEOv2/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-site-overview",
					title: "My sites",
					href: "/SEOv2/20063/Overview/SiteOverview",
					icon: null,
					observeKey: "side-nav-seo-v2-site-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-insights-feed",
					title: "Insights",
					href: "/SEOv2/20063/Insights/Index?period=Custom",
					icon: null,
					observeKey: "side-nav-seo-v2-insights-feed",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-insights-keyword-monitoring",
					title: "Keyword Monitoring",
					href: "/SEOv2/20063/KeywordMonitoring/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-insights-keyword-monitoring",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-activity-plans",
							title: "Activity Plan overview",
							href: "/SEOv2/20063/ActivityPlansOverview/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-activity-plans",
						},
						{
							children: [],
							defaultChildId: null,
							id: "competitor-analysis",
							title: "Competitor Analysis",
							href: "/SEOv2/20063/CompetitorAnalysis/Analysis",
							icon: null,
							observeKey: "side-nav-competitor-analysis",
						},
					],
					defaultChildId: null,
					id: "seo-v2-activity-plans-overview",
					title: "Activity Plans",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-activity-plans-overview",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-recommendations",
							title: "Issues and recommendations",
							href: "/SEOv2/20063/Recommendations/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-recommendations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-groups",
							title: "Groups",
							href: "/SEOv2/20063/SeoGroups/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-groups",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-content-optimization",
							title: "Content optimization",
							href: "/SEOv2/20063/ContentOptimization/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-content-optimization",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-target-pages",
							title: "Target Pages",
							href: "/SEOv2/20063/TargetPages/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-target-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-publish-tools-snippet-preview",
							title: "Search Snippet Preview",
							href: "/SEOv2/20063/PublishTools/SnippetPreview",
							icon: null,
							observeKey: "side-nav-seo-v2-publish-tools-snippet-preview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-similar-pages",
							title: "Duplicate content",
							href: "/SEOv2/20063/Similarity/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-similar-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-anchor-texts",
							title: "Internal Anchor Texts",
							href: "/SEOv2/20063/Similarity/AnchorTexts",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-anchor-texts",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-policies",
							title: "SEO Policies",
							href: "/SEOv2/20063/Overview/Policies",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-policies",
						},
					],
					defaultChildId: null,
					id: "seo-v2-optimize",
					title: "Optimize",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-optimize",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-keyword-discovery",
							title: "Keyword Discovery",
							href: "/SEOv2/20063/KeywordDiscovery/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-keyword-discovery",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-keyword-finder",
							title: "Keyword suggestions",
							href: "/SEOv2/20063/KeywordFinder/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-keyword-finder",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-google-search-console",
							title: "Google Search Console ",
							href: "/SEOv2/20063/ThisYear/SearchConsole/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-google-search-console",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-search-engine-statistics",
							title: "Search Engine Analytics",
							href: "/SEOv2/20063/Custom/SearchEngineStatistics/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-search-engine-statistics",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-organic-search",
							title: "Top-ranking Pages by Keyword",
							href: "/SEOv2/20063/KeywordSearch/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-organic-search",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-organic-keywords-by-domain",
							title: "Top Keywords by Domain",
							href: "/SEOv2/20063/DomainSearch/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-organic-keywords-by-domain",
						},
					],
					defaultChildId: null,
					id: "seo-v2-insights",
					title: "Research",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-insights",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-google-my-business",
					title: "Google My Business",
					href: "/SEOv2/LocalSeo?period=Custom",
					icon: null,
					observeKey: "side-nav-seo-v2-google-my-business",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-overview",
							title: "Backlinks Overview",
							href: "/SEOv2/20063/BacklinksOverview/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-link-profile",
							title: "Link Profile",
							href: "/SEOv2/20063/BacklinksLinkProfile/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-link-profile",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-top-back-links",
							title: "High-value Backlinks",
							href: "/SEOv2/20063/BacklinksTopBacklinks/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-top-back-links",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-high-risk-backlinks",
							title: "Low-value Backlinks",
							href: "/SEOv2/20063/BacklinksHighRiskBacklinks/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-high-risk-backlinks",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-link-reclamation",
							title: "Link Reclamation",
							href: "/SEOv2/20063/BacklinksLinkReclamation/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-link-reclamation",
						},
					],
					defaultChildId: null,
					id: "seo-v2-backlinks",
					title: "Backlinks",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-backlinks",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-integrations",
							title: "Integrations",
							href: "/SEOv2/20063/SeoSettings/Integrations",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-integrations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-keyword-import",
							title: "Bulk Keyword Import",
							href: "/SEOv2/20063/Import/Keywords",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-keyword-import",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-google-search-console-settings",
							title: "Google Search Console settings",
							href: "/SEOv2/SearchConsoleSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-google-search-console-settings",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-local-seo",
							title: "Local SEO settings",
							href: "/SEOv2/LocalSeoSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-local-seo",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-competitor",
							title: "Competitor Settings",
							href: "/SEOv2/20063/CompetitorSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-competitor",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-keyword",
							title: "Keyword Settings",
							href: "/SEOv2/KeywordSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-keyword",
						},
					],
					defaultChildId: null,
					id: "seo-v2-settings",
					title: "SEO Settings",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-settings",
				},
			],
			defaultChildId: "seo-v2-overview",
			id: "seo-v2",
			title: "SEO Enterprise",
			href: "/SEOv2/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M21.6424 19.9372L17.962 16.2568C20.6023 12.7763 20.3423 7.77571 17.1619 4.59532C13.7014 1.13489 8.06075 1.13489 4.60032 4.59532C2.92011 6.27553 2 8.5158 2 10.8761C2 11.2961 2.02 11.6962 2.08001 12.0962C2.08001 12.0962 3.68021 11.7162 4.4003 10.7561C4.4403 9.07587 5.10038 7.49568 6.30053 6.29553C7.56068 5.03537 9.22089 4.39529 10.8811 4.39529C12.5413 4.39529 14.2015 5.03537 15.4617 6.29553C18.002 8.81584 18.002 12.9363 15.4817 15.4767C14.6416 16.3168 13.6214 16.8768 12.5413 17.1569L11.9012 17.4169C11.3211 17.6569 10.8611 18.117 10.6211 18.6971L10.181 19.7372C10.421 19.7572 10.6411 19.7572 10.8811 19.7572C12.7813 19.7572 14.6816 19.1571 16.2618 17.957L19.9422 21.6374C20.1822 21.8774 20.4823 21.9975 20.7823 21.9975C21.0824 21.9975 21.4024 21.8774 21.6224 21.6374C22.1225 21.1774 22.1225 20.4173 21.6424 19.9372Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M12.9827 14.1365C13.2828 14.2565 13.2628 14.6766 12.9827 14.7966L11.1423 15.5767C10.0821 16.0167 9.24196 16.8568 8.80187 17.917C8.54182 18.557 8.24176 19.2571 8.02172 19.7572C7.9017 20.0572 7.48161 20.0572 7.36159 19.7572C7.16155 19.2771 6.8815 18.637 6.64145 18.037C6.16135 16.8968 5.24117 15.9767 4.10095 15.4967L2.38061 14.7966C2.08055 14.6766 2.08055 14.2565 2.38061 14.1365L4.10095 13.4164C5.24117 12.9363 6.16135 12.0162 6.64145 10.8761L7.36159 9.15587C7.48161 8.85584 7.9017 8.85584 8.02172 9.15587L8.80187 10.9961C9.24196 12.0562 10.0821 12.8963 11.1423 13.3364C11.7625 13.6364 12.4626 13.9165 12.9827 14.1365Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-seo-v2",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "analytics-overview",
					title: "Analytics overview",
					href: "/Analytics/30488/Custom/Overview/Index",
					icon: null,
					observeKey: "side-nav-analytics-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "analytics-live-analytics",
					title: "Live analytics",
					href: "/Analytics/30488/Custom/Live/Index",
					icon: null,
					observeKey: "side-nav-analytics-live-analytics",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/KeyMetrics/Index",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-kpi-overview",
							title: "Key performance indicators",
							href: "/Analytics/30488/Custom/KeyMetrics/KpiOverview",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-kpi-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-list",
							title: "Key metrics",
							href: "/Analytics/30488/Custom/KeyMetrics/Metrics",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-list",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-categories",
							title: "Key metrics categories",
							href: "/Analytics/30488/Custom/KeyMetrics/Categories",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-categories",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-academy",
							title: "Key metrics training",
							href: "/Analytics/30488/Custom/KeyMetrics/MetricAcademy",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-academy",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-key-metrics-settings",
							title: "Key metrics settings",
							href: "/Analytics/30488/Custom/KeyMetrics/MetricSettings",
							icon: null,
							observeKey: "side-nav-analytics-key-metrics-settings",
						},
					],
					defaultChildId: null,
					id: "analytics-key-metrics",
					title: "Key metrics",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-key-metrics",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/Users/Summary",
							icon: null,
							observeKey: "side-nav-analytics-visitors-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-countries",
							title: "Countries",
							href: "/Analytics/30488/Custom/Users/Countries",
							icon: null,
							observeKey: "side-nav-analytics-visitors-countries",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-organizations",
							title: "Organizations",
							href: "/Analytics/30488/Custom/Users/Organizations",
							icon: null,
							observeKey: "side-nav-analytics-visitors-organizations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-devices",
							title: "Devices",
							href: "/Analytics/30488/Custom/Users/Devices",
							icon: null,
							observeKey: "side-nav-analytics-visitors-devices",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-operating-systems",
							title: "Operating systems",
							href: "/Analytics/30488/Custom/Users/OperatingSystems",
							icon: null,
							observeKey: "side-nav-analytics-visitors-operating-systems",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-browsers",
							title: "Browsers",
							href: "/Analytics/30488/Custom/Users/Browsers",
							icon: null,
							observeKey: "side-nav-analytics-visitors-browsers",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-visitors-resolution",
							title: "Screen resolutions",
							href: "/Analytics/30488/Custom/Users/Resolution",
							icon: null,
							observeKey: "side-nav-analytics-visitors-resolution",
						},
					],
					defaultChildId: null,
					id: "analytics-visitors",
					title: "Visitors",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-visitors",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/Content/Index",
							icon: null,
							observeKey: "side-nav-analytics-content-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-popular-pages",
							title: "Most popular pages",
							href: "/Analytics/30488/Custom/Content/PopularPages",
							icon: null,
							observeKey: "side-nav-analytics-content-popular-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-unpopular-pages",
							title: "Least popular pages",
							href: "/Analytics/30488/Custom/Content/UnpopularPages",
							icon: null,
							observeKey: "side-nav-analytics-content-unpopular-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-content-pages",
							title: "All pages",
							href: "/Analytics/30488/Custom/Content/ContentPages",
							icon: null,
							observeKey: "side-nav-analytics-content-content-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-pages-without-visits",
							title: "Pages with no visits",
							href: "/Analytics/30488/Custom/PagesNoVisits/Index",
							icon: null,
							observeKey: "side-nav-analytics-content-pages-without-visits",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-page-load-times",
							title: "Page load time",
							href: "/Analytics/30488/Custom/Content/PageLoadTimes",
							icon: null,
							observeKey: "side-nav-analytics-content-page-load-times",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-navigation-depth",
							title: "Navigation depth",
							href: "/Analytics/30488/Custom/Content/NavigationDepth",
							icon: null,
							observeKey: "side-nav-analytics-content-navigation-depth",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-parameters",
							title: "Parameters",
							href: "/Analytics/30488/Custom/Content/Parameters",
							icon: null,
							observeKey: "side-nav-analytics-content-parameters",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-content-groups",
							title: "Groups",
							href: "/Analytics/30488/Custom/Overview/Groups",
							icon: null,
							observeKey: "side-nav-analytics-content-groups",
						},
					],
					defaultChildId: null,
					id: "analytics-content",
					title: "Content",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-content",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/EntriesExits/Index",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-entry-and-exit-rates",
							title: "Entry and exit rates",
							href: "/Analytics/30488/Custom/EntriesExits/EntryExitRate?VisitsFilter=Min100",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-entry-and-exit-rates",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-entry-pages",
							title: "Entry pages",
							href: "/Analytics/30488/Custom/EntriesExits/EntryPages",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-entry-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-exit-pages",
							title: "Exit pages",
							href: "/Analytics/30488/Custom/EntriesExits/ExitPages",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-exit-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-exit-links",
							title: "Outbound links",
							href: "/Analytics/30488/Custom/EntriesExits/ExitLinks",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-exit-links",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-entries-exits-bounce-rates",
							title: "Bounce rates",
							href: "/Analytics/30488/Custom/EntriesExits/BounceRates?VisitsFilter=Min100",
							icon: null,
							observeKey: "side-nav-analytics-entries-exits-bounce-rates",
						},
					],
					defaultChildId: null,
					id: "analytics-entries-exits",
					title: "Entries and exits",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-entries-exits",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/Sources/Index",
							icon: null,
							observeKey: "side-nav-analytics-sources-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-referrers",
							title: "External referring domains",
							href: "/Analytics/30488/Custom/Sources/Referrers",
							icon: null,
							observeKey: "side-nav-analytics-sources-referrers",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-external-referring-pages",
							title: "External referring pages",
							href: "/Analytics/30488/Custom/Sources/ExternalReferringPages",
							icon: null,
							observeKey: "side-nav-analytics-sources-external-referring-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-social-media",
							title: "Social media",
							href: "/Analytics/30488/Custom/Sources/SocialMedia",
							icon: null,
							observeKey: "side-nav-analytics-sources-social-media",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-search-engines",
							title: "Search engines",
							href: "/Analytics/30488/Custom/Sources/SearchEngines",
							icon: null,
							observeKey: "side-nav-analytics-sources-search-engines",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-sources-direct-traffic",
							title: "Direct traffic",
							href: "/Analytics/30488/Custom/Sources/DirectTraffic",
							icon: null,
							observeKey: "side-nav-analytics-sources-direct-traffic",
						},
					],
					defaultChildId: null,
					id: "analytics-sources",
					title: "Traffic sources",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-sources",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-campaigns-overview",
							title: "Campaigns overview",
							href: "/Analytics/30488/Custom/Campaigns/Overview",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-campaigns-monitor",
							title: "Campaign monitor",
							href: "/Analytics/30488/Custom/CampaignMonitor/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-monitor",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-campaigns-comparison",
							title: "Campaign comparison",
							href: "/Analytics/30488/Custom/CampaignComparison/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-comparison",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-campaigns-url-shortener",
							title: "URL shortener",
							href: "/Analytics/30488/Custom/UrlShortener/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-url-shortener",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-campaigns-utm-builder",
							title: "UTM link builder",
							href: "/Analytics/30488/Custom/UtmBuilder/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-utm-builder",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "analytics-campaigns-utm-presets-source-medium-presets",
									title: "Preset groups",
									href: "/Analytics/30488/Custom/UtmPresets/SourceMediumPresets",
									icon: null,
									observeKey: "side-nav-analytics-campaigns-utm-presets-source-medium-presets",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-campaigns-utm-presets-medium-presets",
									title: "utm_medium presets",
									href: "/Analytics/30488/Custom/UtmPresets/MediumPresets",
									icon: null,
									observeKey: "side-nav-analytics-campaigns-utm-presets-medium-presets",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-campaigns-utm-presets-source-presets",
									title: "utm_source presets",
									href: "/Analytics/30488/Custom/UtmPresets/SourcePresets",
									icon: null,
									observeKey: "side-nav-analytics-campaigns-utm-presets-source-presets",
								},
							],
							defaultChildId: null,
							id: "analytics-campaigns-utm-presets",
							title: "UTM presets",
							href: "/Analytics/Custom/Home/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-utm-presets",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "analytics-campaigns-campaign-data-data-pages",
									title: "Landing pages",
									href: "/Analytics/30488/Custom/Campaigns/DataPages",
									icon: null,
									observeKey: "side-nav-analytics-campaigns-campaign-data-data-pages",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-campaigns-campaign-data-data-index",
									title: "UTM parameters",
									href: "/Analytics/30488/Custom/Campaigns/DataIndex",
									icon: null,
									observeKey: "side-nav-analytics-campaigns-campaign-data-data-index",
								},
							],
							defaultChildId: null,
							id: "analytics-campaigns-campaign-data",
							title: "Pages and parameters",
							href: "/Analytics/Custom/Home/Index",
							icon: null,
							observeKey: "side-nav-analytics-campaigns-campaign-data",
						},
					],
					defaultChildId: null,
					id: "analytics-campaigns",
					title: "Campaigns",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-campaigns",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-internal-search-overview",
							title: "Summary",
							href: "/Analytics/30488/Custom/InternalSearch/Summary",
							icon: null,
							observeKey: "side-nav-analytics-internal-search-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-internal-search-all-searches",
							title: "All Searches",
							href: "/Analytics/30488/Custom/InternalSearch/AllSearches",
							icon: null,
							observeKey: "side-nav-analytics-internal-search-all-searches",
						},
					],
					defaultChildId: null,
					id: "internal-search-expandable",
					title: "Internal searches",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-internal-search-expandable",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-summary",
							title: "Summary",
							href: "/Analytics/30488/Custom/Behaviour/Index",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-summary",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-user-journeys",
							title: "User journeys",
							href: "/Analytics/30488/Custom/Behaviour/UserJourneys",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-user-journeys",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-behaviour-tracking",
							title: "Behavior tracking",
							href: "/Analytics/30488/Custom/Behaviour/BehaviourTrackingStart",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-behaviour-tracking",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-behaviour-map",
							title: "Behavior maps",
							href: "/Analytics/30488/Custom/BehaviourMap/Index",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-behaviour-map",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-funnels",
							title: "Funnels",
							href: "/Analytics/30488/Custom/Funnel/Overview",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-funnels",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-visit-depth",
							title: "Visit depth",
							href: "/Analytics/30488/Custom/Behaviour/VisitDepth",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-visit-depth",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-visit-length",
							title: "Visit length",
							href: "/Analytics/30488/Custom/Behaviour/VisitLength",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-visit-length",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-visit-per-month",
							title: "Visits by day of month",
							href: "/Analytics/30488/Custom/Behaviour/VisitMonth",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-visit-per-month",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-visit-week",
							title: "Visits by weekdays",
							href: "/Analytics/30488/Custom/Behaviour/VisitWeek",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-visit-week",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-behaviour-visit-hour",
							title: "Visits by hour of day",
							href: "/Analytics/30488/Custom/Behaviour/VisitHour",
							icon: null,
							observeKey: "side-nav-analytics-behaviour-visit-hour",
						},
					],
					defaultChildId: null,
					id: "analytics-behaviour",
					title: "Behavior",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-behaviour",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-event-tracking-visit-hour",
							title: "Events",
							href: "/Analytics/30488/Custom/EventTracking/Index",
							icon: null,
							observeKey: "side-nav-analytics-event-tracking-visit-hour",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-event-tracking-pages",
							title: "Pages with events",
							href: "/Analytics/30488/Custom/EventTracking/Pages",
							icon: null,
							observeKey: "side-nav-analytics-event-tracking-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-event-tracking-categories",
							title: "Categories",
							href: "/Analytics/30488/Custom/EventTracking/Categories",
							icon: null,
							observeKey: "side-nav-analytics-event-tracking-categories",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-event-tracking-actions",
							title: "Actions",
							href: "/Analytics/30488/Custom/EventTracking/Actions",
							icon: null,
							observeKey: "side-nav-analytics-event-tracking-actions",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-event-tracking-labels",
							title: "Labels",
							href: "/Analytics/30488/Custom/EventTracking/Labels",
							icon: null,
							observeKey: "side-nav-analytics-event-tracking-labels",
						},
					],
					defaultChildId: null,
					id: "analytics-event-tracking",
					title: "Event tracking",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-event-tracking",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-feedback-list",
							title: "Feedback",
							href: "/Analytics/30488/Custom/Feedback/Feedback",
							icon: null,
							observeKey: "side-nav-analytics-feedback-list",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-feedback-pages",
							title: "Pages with feedback",
							href: "/Analytics/30488/Custom/Feedback/PagesWithFeedback",
							icon: null,
							observeKey: "side-nav-analytics-feedback-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-feedback-ignored",
							title: "Ignored feedback",
							href: "/Analytics/30488/Custom/Feedback/FeedbackIgnored",
							icon: null,
							observeKey: "side-nav-analytics-feedback-ignored",
						},
					],
					defaultChildId: null,
					id: "analytics-feedback",
					title: "User feedback",
					href: "/Analytics/Custom/Home/Index",
					icon: null,
					observeKey: "side-nav-analytics-feedback",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "analytics-settings-filters",
							title: "Filters",
							href: "/Settings/AnalyticsFilter/Index",
							icon: null,
							observeKey: "side-nav-analytics-settings-filters",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-settings-site-settings",
							title: "Site settings",
							href: "/Settings/AnalyticsSiteConfiguration/Index",
							icon: null,
							observeKey: "side-nav-analytics-settings-site-settings",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-settings-feedback",
							title: "Feedback",
							href: "/Settings/AnalyticsFeedback/Index",
							icon: null,
							observeKey: "side-nav-analytics-settings-feedback",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-config-ip-groups",
									title: "IP groups",
									href: "/Settings/AnalyticsIp/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-config-ip-groups",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-config-parameters",
									title: "Parameters/campaigns",
									href: "/Settings/30488/AnalyticsParameters/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-config-parameters",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-config-url-search-replace",
									title: "URL search replace",
									href: "/Settings/30488/AnalyticsUrlSearchReplace/Index?period=Custom",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-config-url-search-replace",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-config-domains",
									title: "Domain settings",
									href: "/Settings/30488/AnalyticsDomain/Index?period=Custom",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-config-domains",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-config-site-log-config",
									title: "Site configuration",
									href: "/Settings/AnalyticsSiteLogConfiguration/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-config-site-log-config",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-data-exclusion",
									title: "Data exclusion settings",
									href: "/Settings/AnalyticsDataExclusion/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-data-exclusion",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-tracking-script",
									title: "Analytics script",
									href: "/Settings/Analytics/Script",
									icon: null,
									observeKey: "side-nav-analytics-settings-tracking-script",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-endpoints",
									title: "Endpoint settings",
									href: "/Analytics/Custom/Endpoints/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-endpoints",
								},
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-cookieless",
									title: "Cookieless tracking",
									href: "/Analytics/Custom/Cookieless/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-cookieless",
								},
							],
							defaultChildId: null,
							id: "analytics-settings-tracking-config",
							title: "Tracking",
							href: null,
							icon: null,
							observeKey: "side-nav-analytics-settings-tracking-config",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "analytics-settings-data-configurations-privacy",
									title: "Visitor privacy",
									href: "/Settings/AnalyticsPrivacySettings/Index",
									icon: null,
									observeKey: "side-nav-analytics-settings-data-configurations-privacy",
								},
							],
							defaultChildId: null,
							id: "analytics-settings-data-configurations",
							title: "Privacy",
							href: null,
							icon: null,
							observeKey: "side-nav-analytics-settings-data-configurations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "analytics-settings-behaviour-map-screenshot",
							title: "Behavior map screenshot settings",
							href: "/Settings/30488/AnalyticsBehaviourMap/Index",
							icon: null,
							observeKey: "side-nav-analytics-settings-behaviour-map-screenshot",
						},
						{
							children: [],
							defaultChildId: null,
							id: "integrations-single-digital-gateway",
							title: "Single Digital Gateway Connection",
							href: "/Analytics/Custom/SingleDigitalGateway/Index",
							icon: null,
							observeKey: "side-nav-integrations-single-digital-gateway",
						},
					],
					defaultChildId: null,
					id: "analytics-settings",
					title: "Analytics settings",
					href: null,
					icon: null,
					observeKey: "side-nav-analytics-settings",
				},
			],
			defaultChildId: "analytics-overview",
			id: "analytics",
			title: "Marketing Analytics",
			href: "/Analytics/Custom/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M13.6942 13.3879H10.3078C9.86745 13.3879 9.51045 13.7449 9.51045 14.1853V21.0237C9.51045 21.464 9.86745 21.821 10.3078 21.821H13.6942C14.1346 21.821 14.4916 21.464 14.4916 21.0237V14.1853C14.4916 13.7449 14.1346 13.3879 13.6942 13.3879Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M6.18373 16.9711H2.79738C2.357 16.9711 2 17.3281 2 17.7685V21.0236C2 21.464 2.357 21.821 2.79738 21.821H6.18373C6.62411 21.821 6.98111 21.464 6.98111 21.0236V17.7685C6.98111 17.3281 6.62411 16.9711 6.18373 16.9711Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M21.2026 9.83057H17.8163C17.3759 9.83057 17.0189 10.1876 17.0189 10.6279V21.0236C17.0189 21.464 17.3759 21.821 17.8163 21.821H21.2026C21.643 21.821 22 21.464 22 21.0236V10.6279C22 10.1876 21.643 9.83057 21.2026 9.83057Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M19.5085 2.17896C18.2677 2.17896 17.2615 3.18512 17.2615 4.42592C17.2615 4.48558 17.2655 4.54523 17.2714 4.6029L13.561 6.35474C13.1573 5.965 12.6085 5.72439 12.002 5.72439C10.7612 5.72439 9.75503 6.73056 9.75503 7.97136C9.75503 8.03101 9.75901 8.09067 9.76299 8.14833L6.05251 9.90017C5.64885 9.51043 5.10003 9.26784 4.49355 9.26784C3.25275 9.26784 2.24658 10.274 2.24658 11.5148C2.24658 12.7556 3.25275 13.7618 4.49355 13.7618C5.73435 13.7618 6.74052 12.7556 6.74052 11.5148C6.74052 11.4552 6.73654 11.3955 6.73057 11.3378L10.4411 9.586C10.8447 9.97574 11.3955 10.2183 12.002 10.2183C13.2428 10.2183 14.249 9.21216 14.249 7.97136C14.249 7.91171 14.245 7.85205 14.239 7.79439L17.9495 6.04255C18.3532 6.43229 18.902 6.67488 19.5085 6.67488C20.7493 6.67488 21.7554 5.66871 21.7554 4.42791C21.7554 3.18711 20.7493 2.18094 19.5085 2.18094V2.17896ZM19.5085 5.68263C18.8165 5.68263 18.2537 5.1199 18.2537 4.42791C18.2537 3.73592 18.8165 3.17319 19.5085 3.17319C20.2004 3.17319 20.7632 3.73592 20.7632 4.42791C20.7632 5.1199 20.2004 5.68263 19.5085 5.68263Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-analytics",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "adwords-overview",
					title: "Ads overview",
					href: "/PPC/56120/LastSevenDays/Overview/Index",
					icon: null,
					observeKey: "side-nav-adwords-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "adwords-insights",
					title: "Insights",
					href: "/PPC/56120/LastSevenDays/Insights/Index",
					icon: null,
					observeKey: "side-nav-adwords-insights",
				},
				{
					children: [],
					defaultChildId: null,
					id: "adwords-quality-analysis",
					title: "Quality score analysis",
					href: "/PPC/56120/LastSevenDays/QualityAnalysis/Index",
					icon: null,
					observeKey: "side-nav-adwords-quality-analysis",
				},
				{
					children: [],
					defaultChildId: null,
					id: "adwords-landing-page-optimization",
					title: "Landing page optimization",
					href: "/PPC/56120/LastSevenDays/LandingPageOptimization/Index",
					icon: null,
					observeKey: "side-nav-adwords-landing-page-optimization",
				},
				{
					children: [],
					defaultChildId: null,
					id: "adwords-budget-analysis",
					title: "Budget analysis",
					href: "/PPC/56120/LastSevenDays/BudgetAnalysis/Index",
					icon: null,
					observeKey: "side-nav-adwords-budget-analysis",
				},
				{
					children: [],
					defaultChildId: null,
					id: "adwords-campaign-insights",
					title: "Data explorer",
					href: "/PPC/56120/LastSevenDays/DataExplorer/Index",
					icon: null,
					observeKey: "side-nav-adwords-campaign-insights",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "adwords-reporting-account-overview",
							title: "Account overview",
							href: "/PPC/56120/LastSevenDays/Reporting/Index",
							icon: null,
							observeKey: "side-nav-adwords-reporting-account-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "adwords-reporting-history",
							title: "History",
							href: "/PPC/56120/LastSevenDays/Reporting/History",
							icon: null,
							observeKey: "side-nav-adwords-reporting-history",
						},
						{
							children: [],
							defaultChildId: null,
							id: "adwords-reporting-location-overview",
							title: "Location overview",
							href: "/PPC/56120/LastSevenDays/Reporting/LocationOverview",
							icon: null,
							observeKey: "side-nav-adwords-reporting-location-overview",
						},
					],
					defaultChildId: null,
					id: "adwords-reporting",
					title: "Monitor ads performance",
					href: "/PPC/Home/Index",
					icon: null,
					observeKey: "side-nav-adwords-reporting",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "adwords-settings-recommendation-thresholds",
							title: "Set targets and thresholds",
							href: "/PPC/56120/LastSevenDays/RecommendationThresholds/Index",
							icon: null,
							observeKey: "side-nav-adwords-settings-recommendation-thresholds",
						},
						{
							children: [],
							defaultChildId: null,
							id: "adwords-settings-manage-account",
							title: "Manage accounts",
							href: "/PPC/AdwordsSettings/Index",
							icon: null,
							observeKey: "side-nav-adwords-settings-manage-account",
						},
						{
							children: [],
							defaultChildId: null,
							id: "adwords-settings-integrations",
							title: "Connect Analytics",
							href: "/PPC/56120/LastSevenDays/AdwordsAnalyticsSettings/Integrations",
							icon: null,
							observeKey: "side-nav-adwords-settings-integrations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "adwords-settings-content-check-whitelist",
							title: "Ads dictionary",
							href: "/PPC/56120/LastSevenDays/AdwordsAnalyticsSettings/ContentCheckWhitelist",
							icon: null,
							observeKey: "side-nav-adwords-settings-content-check-whitelist",
						},
					],
					defaultChildId: null,
					id: "adwords-settings",
					title: "Settings",
					href: "/PPC/AdwordsSettings/Index",
					icon: null,
					observeKey: "side-nav-adwords-settings",
				},
			],
			defaultChildId: "adwords-overview",
			id: "adwords",
			title: "Ads",
			href: "/PPC/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M10.7675 10.8579C10.2508 10.9388 9.90472 11.4219 9.98561 11.9387L11.3001 20.3288C11.381 20.8456 11.8596 21.2006 12.3764 21.1197C12.6415 21.077 12.8662 20.922 13.0077 20.7085V20.713L14.2818 18.6323L16.3826 21.5803C16.7714 22.1331 17.5286 22.2566 18.0813 21.8679C18.6228 21.4815 18.7464 20.722 18.36 20.1805L16.1782 17.1134L18.5689 16.2663C18.9419 16.0933 19.1711 15.7046 19.1059 15.2889C19.0543 14.9608 18.8318 14.7137 18.5554 14.5923L11.4349 11.004C11.2259 10.8939 11.0125 10.8197 10.7698 10.8579H10.7675Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.8743 1.90613H3.12347C2.50331 1.90613 2 2.40944 2 3.0296V17.9987C2 18.6189 2.50331 19.1222 3.12347 19.1222H9.97214L8.87338 12.114C8.69588 10.986 9.46658 9.92545 10.5923 9.74794C10.6957 9.73221 10.8013 9.72323 10.9046 9.72323C11.2462 9.72323 11.5787 9.80861 11.8798 9.97264C11.9 9.98162 11.9202 9.99061 11.9405 9.9996L19.0543 13.5677C19.1172 13.5992 19.1756 13.6374 19.2318 13.6778C19.7598 13.9879 20.1216 14.5092 20.2159 15.1136C20.344 15.9248 19.9778 16.709 19.2924 17.1471C19.1284 17.2729 17.9645 17.6841 17.9645 17.6841L18.9869 19.1199H20.8765C21.4967 19.1199 22 18.6166 22 17.9965V3.0296C22 2.40944 21.4967 1.90613 20.8765 1.90613H20.8743ZM4.37726 5.15969C3.86496 5.15969 3.44703 4.74401 3.44703 4.22946C3.44703 3.71491 3.86271 3.29923 4.37726 3.29923C4.89181 3.29923 5.30749 3.71491 5.30749 4.22946C5.30749 4.74401 4.89181 5.15969 4.37726 5.15969ZM7.09156 5.15969C6.57926 5.15969 6.16133 4.74401 6.16133 4.22946C6.16133 3.71491 6.57701 3.29923 7.09156 3.29923C7.60611 3.29923 8.0218 3.71491 8.0218 4.22946C8.0218 4.74401 7.60611 5.15969 7.09156 5.15969ZM9.80586 5.15969C9.29356 5.15969 8.87563 4.74401 8.87563 4.22946C8.87563 3.71491 9.29131 3.29923 9.80586 3.29923C10.3204 3.29923 10.7361 3.71491 10.7361 4.22946C10.7361 4.74401 10.3204 5.15969 9.80586 5.15969ZM19.5149 5.15969H12.9134C12.4056 5.15969 11.9944 4.7485 11.9944 4.2407C11.9944 3.73289 12.4056 3.3217 12.9134 3.3217H19.5149C20.0227 3.3217 20.4339 3.73289 20.4339 4.2407C20.4339 4.7485 20.0227 5.15969 19.5149 5.15969Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-adwords",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "dataprivacy-overview",
					title: "Overview",
					href: "/Gdpr/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-dataprivacy-overview",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "dataprivacy-personal-data-my-sites",
							title: "My Sites",
							href: "/Gdpr/20063/PersonalData/MySites",
							icon: null,
							observeKey: "side-nav-dataprivacy-personal-data-my-sites",
						},
						{
							children: [],
							defaultChildId: null,
							id: "dataprivacy-personal-data-on-pages-and-docs",
							title: "Content with Personal Data",
							href: "/Gdpr/20063/PersonalData/PagesAndDocumentsWithPersonalData",
							icon: null,
							observeKey: "side-nav-dataprivacy-personal-data-on-pages-and-docs",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "dataprivacy-personal-data-types-cpr",
									title: "Personal ID numbers",
									onClick: function () {
										console.log("dataprivacy-personal-data-types-cpr clicked");
									},
									icon: null,
									observeKey: "side-nav-dataprivacy-personal-data-types-cpr",
								},
								{
									children: [],
									defaultChildId: null,
									id: "dataprivacy-personal-data-types-phone-number",
									title: "Phone numbers",
									href: "/Gdpr/20063/PersonalData/DataTypePhoneNumer",
									icon: null,
									observeKey: "side-nav-dataprivacy-personal-data-types-phone-number",
								},
								{
									children: [],
									defaultChildId: null,
									id: "dataprivacy-personal-data-types-email",
									title: "Email addresses",
									href: "/Gdpr/20063/PersonalData/DataTypeEmail",
									icon: null,
									observeKey: "side-nav-dataprivacy-personal-data-types-email",
								},
							],
							defaultChildId: null,
							id: "dataprivacy-personal-data-data-types",
							title: "Personal Data",
							href: null,
							icon: null,
							observeKey: "side-nav-dataprivacy-personal-data-data-types",
						},
					],
					defaultChildId: null,
					id: "dataprivacy-personal-data",
					title: "Personal Data Inventory",
					href: null,
					icon: null,
					observeKey: "side-nav-dataprivacy-personal-data",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "dataprivacy-universal-search-fancy",
							title: "Search",
							href: "/Gdpr/UniversalSearch/Index",
							icon: null,
							observeKey: "side-nav-dataprivacy-universal-search-fancy",
						},
						{
							children: [],
							defaultChildId: null,
							id: "dataprivacy-universal-search-tracking",
							title: "Tracked Search Terms",
							href: "/Gdpr/GdprTracking/Index",
							icon: null,
							observeKey: "side-nav-dataprivacy-universal-search-tracking",
						},
					],
					defaultChildId: null,
					id: "dataprivacy-universal-search",
					title: "Universal Search",
					href: null,
					icon: null,
					observeKey: "side-nav-dataprivacy-universal-search",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dataprivacy-domains-and-ips",
					title: "Domain Locator",
					href: "/Gdpr/DomainsAndIPs/Index",
					icon: null,
					observeKey: "side-nav-dataprivacy-domains-and-ips",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dataprivacy-cookies",
					title: "Cookie Inventory",
					href: "/Gdpr/20063/Cookie/Index",
					icon: null,
					observeKey: "side-nav-dataprivacy-cookies",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dataprivacy-policy",
					title: "Data Privacy and Security Policies",
					href: "/Gdpr/20063/Overview/Policies",
					icon: null,
					observeKey: "side-nav-dataprivacy-policy",
				},
				{
					children: [],
					defaultChildId: null,
					id: "dataprivacy-action-log",
					title: "User Actions Log",
					href: "/Gdpr/GdprActionLog/Index",
					icon: null,
					observeKey: "side-nav-dataprivacy-action-log",
				},
				{
					children: [
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "dataprivacy-data-privacy-settings-personal-identification-numbers-site-config",
									title: "Country-specific format",
									href: "/Gdpr/DataPrivacySettings/Index",
									icon: null,
									observeKey:
										"side-nav-dataprivacy-data-privacy-settings-personal-identification-numbers-site-config",
								},
								{
									children: [],
									defaultChildId: null,
									id: "dataprivacy-data-privacy-settings-personal-identification-numbers-recipients",
									title: "Email Alerts",
									href: "/Gdpr/DataPrivacySettings/NotificationRecipients",
									icon: null,
									observeKey:
										"side-nav-dataprivacy-data-privacy-settings-personal-identification-numbers-recipients",
								},
							],
							defaultChildId: null,
							id: "dataprivacy-data-privacy-settings-personal-identification-numbers",
							title: "Personal Identification Numbers",
							href: null,
							icon: null,
							observeKey:
								"side-nav-dataprivacy-data-privacy-settings-personal-identification-numbers",
						},
					],
					defaultChildId: null,
					id: "dataprivacy-data-privacy-settings",
					title: "Data Privacy Settings",
					href: null,
					icon: null,
					observeKey: "side-nav-dataprivacy-data-privacy-settings",
				},
			],
			defaultChildId: "dataprivacy-overview",
			id: "gdpr",
			title: "Data Privacy",
			href: "/Gdpr/20063/Overview/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M19.7858 5.12234C15.7828 5.12234 12.4403 2.18014 12.4403 2.18014C12.3202 2.06005 12.1801 2 12.02 2C11.8399 2 11.6798 2.06005 11.5597 2.18014C11.5597 2.18014 8.21716 5.12234 4.21416 5.12234C3.85389 5.12234 3.55367 5.42257 3.55367 5.78284V11.2269C3.55367 14.1891 4.9347 19.0928 11.7398 21.955C11.8999 22.015 12.1001 22.015 12.2602 21.955C19.0853 19.0928 20.4463 14.2092 20.4463 11.2269V5.78284C20.4463 5.40255 20.1461 5.12234 19.7858 5.12234ZM12 17.7118C12 18.1321 11.5397 18.3923 11.1794 18.1721C9.53815 17.1313 6.09557 14.2492 6.69602 8.68501C6.71604 8.42482 6.9362 8.22467 7.1964 8.20465C7.97698 8.14461 9.67826 7.88441 11.1594 6.8036C11.5196 6.54341 12 6.8036 12 7.24393V17.7118Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-gdpr",
		},
		{
			children: [
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "support-tools-site-configuration-history",
							title: "Site Configuration History",
							href: "/SupportTools/SiteConfigHistory/20063",
							icon: null,
							observeKey: "side-nav-support-tools-site-configuration-history",
						},
						{
							children: [],
							defaultChildId: null,
							id: "support-tools-linkcheck-history",
							title: "Linkcheck History",
							href: "/SupportTools/LinkcheckHistory/20063",
							icon: null,
							observeKey: "side-nav-support-tools-linkcheck-history",
						},
						{
							children: [],
							defaultChildId: null,
							id: "support-tools-connection-tester",
							title: "Connection Tester",
							href: "/SupportTools/ConnectionTester/20063",
							icon: null,
							observeKey: "side-nav-support-tools-connection-tester",
						},
					],
					defaultChildId: null,
					id: "support-tools-site-group",
					title: "Site",
					href: null,
					icon: null,
					observeKey: "side-nav-support-tools-site-group",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "support-tools-a11y-false-positive-review",
							title: "Review false positive reports",
							href: "/SupportTools/A11YSupport/DecisionSupport?siteId=20063",
							icon: null,
							observeKey: "side-nav-support-tools-a11y-false-positive-review",
						},
					],
					defaultChildId: null,
					id: "support-tools-a11y-support",
					title: "Accessibility",
					href: null,
					icon: null,
					observeKey: "side-nav-support-tools-a11y-support",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "support-tools-qa-spellcheck-capture-tags",
							title: "Spellcheck Exclusions",
							href: "/SupportTools/SpellcheckExclusions",
							icon: null,
							observeKey: "side-nav-support-tools-qa-spellcheck-capture-tags",
						},
					],
					defaultChildId: null,
					id: "support-tools-qa",
					title: "QA",
					href: null,
					icon: null,
					observeKey: "side-nav-support-tools-qa",
				},
			],
			defaultChildId: null,
			id: "support-tools",
			title: "Technical Support",
			href: "/SupportTools/Index/20063",
			icon: '\r\n\t\t\t\t\t<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">\r\n\t\t\t\t\t\t<path fill-rule="evenodd" clip-rule="evenodd" d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM12.8866 10.822L17.8428 15.8082C18.0607 15.9726 18.0607 16.3562 17.7884 16.5754L16.5357 17.8356C16.3179 18.0548 15.9911 18.0548 15.7732 17.8356L10.817 12.8494C9.56433 13.3425 8.0938 13.0685 7.05898 12.0274C5.9697 10.9316 5.69738 9.28773 6.35095 7.97268L8.74737 10.3288L10.3813 8.685L8.03933 6.32885C9.34647 5.72612 10.9804 5.94529 12.0697 7.04118C13.1045 8.08226 13.3768 9.5617 12.8866 10.822Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-support-tools",
		},
	],
	sideBottomNavigation: [
		{
			children: [
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "settings-users-manage-users",
							title: "Manage users",
							href: "/Settings/Users/Index",
							icon: null,
							observeKey: "side-nav-settings-users-manage-users",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-users-my-profile",
							title: "My profile",
							href: "/Settings/Users/MyProfile",
							icon: null,
							observeKey: "side-nav-settings-users-my-profile",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-users-user-action-log",
							title: "User action log",
							href: "/Settings/Users/ActionLog",
							icon: null,
							observeKey: "side-nav-settings-users-user-action-log",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-users-roles",
							title: "Roles",
							href: "/Settings/Permissions/Roles",
							icon: null,
							observeKey: "side-nav-settings-users-roles",
						},
					],
					defaultChildId: null,
					id: "settings-users",
					title: "Users",
					href: null,
					icon: null,
					observeKey: "side-nav-settings-users",
				},
				{
					children: [],
					defaultChildId: null,
					id: "site-management-sites",
					title: "Sites",
					href: "/Settings/Sites/v2",
					icon: null,
					observeKey: "side-nav-site-management-sites",
				},
				{
					children: [],
					defaultChildId: null,
					id: "settings-groups",
					title: "Groups",
					href: "/Settings/73617/Groups/Index",
					icon: null,
					observeKey: "side-nav-settings-groups",
				},
				{
					children: [],
					defaultChildId: null,
					id: "settings-tags",
					title: "Tags",
					href: "/Settings/Tags/Index",
					icon: null,
					observeKey: "side-nav-settings-tags",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "settings-content-crawl",
							title: "Crawl settings",
							href: "/Settings/QualityAssurance/Sites",
							icon: null,
							observeKey: "side-nav-settings-content-crawl",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-a11y-site-target",
							title: "Account-wide Accessibility site target",
							href: "/Settings/A11YNextGen/SiteTarget",
							icon: null,
							observeKey: "side-nav-settings-a11y-site-target",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-content-readability",
							title: "Readability",
							href: "/Settings/QualityAssurance/Readability",
							icon: null,
							observeKey: "side-nav-settings-content-readability",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-content-spelling",
							title: "Spellcheck preferences",
							href: "/Settings/QualityAssurance/Spelling",
							icon: null,
							observeKey: "side-nav-settings-content-spelling",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-content-priority-providers",
							title: "Priority data providers",
							href: "/Settings/PriorityProvider/Index",
							icon: null,
							observeKey: "side-nav-settings-content-priority-providers",
						},
					],
					defaultChildId: null,
					id: "settings-content",
					title: "Content",
					href: null,
					icon: null,
					observeKey: "side-nav-settings-content",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "settings-authentication-and-security-password-policy",
							title: "Password requirements",
							href: "/Settings/Users/PasswordPolicy",
							icon: null,
							observeKey: "side-nav-settings-authentication-and-security-password-policy",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-authentication-and-security-sso",
							title: "Single sign-on",
							href: "/Settings/Sso2/Overview",
							icon: null,
							observeKey: "side-nav-settings-authentication-and-security-sso",
						},
					],
					defaultChildId: null,
					id: "settings-authentication-and-security",
					title: "Authentication and security",
					href: null,
					icon: null,
					observeKey: "side-nav-settings-authentication-and-security",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "settings-crawler-site-overview",
							title: "Site overview",
							href: "/Settings/CrawlerManagement/Overview",
							icon: null,
							observeKey: "side-nav-settings-crawler-site-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-crawler-crawl-history",
							title: "Scan history",
							href: "/Settings/CrawlerManagement/History/20063",
							icon: null,
							observeKey: "side-nav-settings-crawler-crawl-history",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-crawler-site-content-settings",
							title: "Site content settings",
							href: "/Settings/CrawlerManagement/73617/SiteScope",
							icon: null,
							observeKey: "side-nav-settings-crawler-site-content-settings",
						},
					],
					defaultChildId: null,
					id: "settings-crawler",
					title: "Crawler management",
					href: null,
					icon: null,
					observeKey: "side-nav-settings-crawler",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "integrations-overview",
							title: "Integrations overview",
							href: "/Integrations/Overview/Index",
							icon: null,
							observeKey: "side-nav-integrations-overview",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "integrations-project-management-connections",
									title: "Connections",
									href: "/Integrations/IssueTracker/Index",
									icon: null,
									observeKey: "side-nav-integrations-project-management-connections",
								},
								{
									children: [],
									defaultChildId: null,
									id: "integrations-project-management-manage-tasks",
									title: "Tasks",
									href: "/Integrations/IssueTracker/WorkItems",
									icon: null,
									observeKey: "side-nav-integrations-project-management-manage-tasks",
								},
							],
							defaultChildId: null,
							id: "integrations-project-management",
							title: "Task management",
							href: "/Integrations/Home/Index",
							icon: null,
							observeKey: "side-nav-integrations-project-management",
						},
						{
							children: [],
							defaultChildId: null,
							id: "integrations-marketing-automation",
							title: "Marketing automation",
							href: "/Integrations/Overview/ManageLandingPageIntegrations",
							icon: null,
							observeKey: "side-nav-integrations-marketing-automation",
						},
						{
							children: [],
							defaultChildId: null,
							id: "integrations-cms-plugin",
							title: "CMS Plugin setup",
							href: "/Integrations/Overlay/Index",
							icon: null,
							observeKey: "side-nav-integrations-cms-plugin",
						},
						{
							children: [
								{
									children: [],
									defaultChildId: null,
									id: "integrations-api-keys",
									title: "API keys",
									href: "/Integrations/Api/ManageApiKeys",
									icon: null,
									observeKey: "side-nav-integrations-api-keys",
								},
								{
									children: [],
									defaultChildId: null,
									id: "integrations-api-terms",
									title: "API terms",
									href: "/Integrations/Api/ApiTermsAndConditions",
									icon: null,
									observeKey: "side-nav-integrations-api-terms",
								},
							],
							defaultChildId: null,
							id: "integrations-api",
							title: "API",
							href: "/Integrations/Home/Index",
							icon: null,
							observeKey: "side-nav-integrations-api",
						},
						{
							children: [],
							defaultChildId: null,
							id: "integrations-cms-deeplinks",
							title: "CMS deeplinks",
							href: "/Integrations/CmsDeepLinks/Index",
							icon: null,
							observeKey: "side-nav-integrations-cms-deeplinks",
						},
						{
							children: [],
							defaultChildId: null,
							id: "integrations-share-bar",
							title: "Share widget",
							href: "/Integrations/ShareBar/Index",
							icon: null,
							observeKey: "side-nav-integrations-share-bar",
						},
					],
					defaultChildId: null,
					id: "integrations",
					title: "Integrations ",
					href: "/Integrations/Overview/Index",
					icon: null,
					observeKey: "side-nav-integrations",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "key-pages-sites",
							title: "Sites",
							href: "/KeyPages/73617/KeyPages/Sites",
							icon: null,
							observeKey: "side-nav-key-pages-sites",
						},
						{
							children: [],
							defaultChildId: null,
							id: "key-pages-overview",
							title: "Pages",
							href: "/KeyPages/73617/KeyPages/Pages",
							icon: null,
							observeKey: "side-nav-key-pages-overview",
						},
					],
					defaultChildId: null,
					id: "key-pages",
					title: "Key Pages",
					href: "/KeyPages/Home/Index",
					icon: null,
					observeKey: "side-nav-key-pages",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "settings-other-shared-xports",
							title: "Shared Exports",
							href: "/Settings/ExportsSettings/Index",
							icon: null,
							observeKey: "side-nav-settings-other-shared-xports",
						},
						{
							children: [],
							defaultChildId: null,
							id: "settings-other-theme",
							title: "Application Themes",
							href: "/Settings/Theme/Index",
							icon: null,
							observeKey: "side-nav-settings-other-theme",
						},
					],
					defaultChildId: null,
					id: "settings-other",
					title: "Other",
					href: null,
					icon: null,
					observeKey: "side-nav-settings-other",
				},
			],
			defaultChildId: "settings-users-my-profile",
			id: "settings",
			title: "Settings",
			href: "/Settings/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M10.5 2C10.02 2 9.62 2.34 9.52 2.8L9.18 4.52C8.36 4.84 7.6 5.26 6.94 5.82L5.3 5.26C4.84 5.1 4.34 5.3 4.1 5.7L2.58 8.3C2.34 8.72 2.42 9.24 2.78 9.56L4.1 10.7C4.04 11.14 4 11.56 4 12C4 12.44 4.04 12.86 4.12 13.3L2.8 14.44C2.44 14.76 2.36 15.28 2.6 15.7L4.1 18.32C4.34 18.74 4.84 18.92 5.3 18.76L6.94 18.2C7.6 18.74 8.36 19.18 9.18 19.48L9.52 21.2C9.62 21.66 10.02 22 10.5 22H13.52C14 22 14.4 21.66 14.5 21.2L14.84 19.48C15.66 19.16 16.42 18.74 17.08 18.18L18.72 18.74C19.18 18.9 19.68 18.7 19.92 18.3L21.42 15.68C21.66 15.26 21.58 14.74 21.22 14.42L19.9 13.28C19.96 12.86 20.02 12.42 20.02 11.98C20.02 11.54 19.98 11.12 19.9 10.68L21.22 9.54C21.58 9.22 21.66 8.7 21.42 8.28L19.92 5.66C19.68 5.24 19.18 5.06 18.72 5.22L17.08 5.78C16.42 5.24 15.66 4.8 14.84 4.5L14.5 2.8C14.4 2.32 14 2 13.52 2H10.5ZM12 8.68C13.82 8.68 15.32 10.16 15.32 12C15.32 13.84 13.82 15.32 12 15.32C10.18 15.32 8.68 13.82 8.68 12C8.68 10.18 10.18 8.68 12 8.68Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-settings",
		},
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "help-center",
					title: "Help Center",
					href: "https://help.siteimprove.com/support/home",
					icon: null,
					observeKey: "top-nav-help-center",
				},
				{
					children: [],
					defaultChildId: null,
					id: "help-frontier",
					title: "Frontier",
					href: "/App/Home/Frontier/1",
					icon: null,
					observeKey: "top-nav-help-frontier",
				},
			],
			defaultChildId: null,
			id: "help",
			title: "Help",
			href: null,
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.99 2C6.65063 2 2.29115 5.91954 2.29115 10.739C2.29115 11.0589 2.31114 11.3589 2.35114 11.6589C2.93107 17.0182 8.43042 20.7978 9.53029 21.4977L10.0502 21.8377C11.0301 22.3776 11.0101 21.3977 11.0301 21.3977V19.4179C16.8094 19.9579 21.7089 15.8384 21.7089 10.739C21.6889 5.91954 17.3294 2 11.99 2ZM11.71 16.8182C11.0501 16.7583 10.5102 16.2183 10.4502 15.5584C10.3702 14.6585 11.1101 13.9186 12.01 13.9986C12.6699 14.0586 13.2099 14.5985 13.2898 15.2584C13.3498 16.1583 12.6099 16.8982 11.71 16.8182ZM13.0499 12.3388C12.9899 12.5388 12.8099 12.6787 12.6099 12.6787H11.2301C10.9301 12.6787 10.7102 12.3988 10.7701 12.0988C11.2101 10.339 13.5098 9.6391 13.5098 8.13928C13.5098 7.37936 12.9499 6.91942 11.99 6.91942C11.1701 6.91942 10.5702 7.31937 10.2102 7.97929C10.0902 8.19927 9.79026 8.27926 9.57029 8.13928L8.39042 7.45936C8.17045 7.33937 8.09046 7.0594 8.21045 6.83943C8.97036 5.4196 10.4502 4.65969 12.07 4.65969C13.9498 4.65969 15.8095 5.79955 15.8095 7.9593C15.8095 10.259 13.4298 11.0789 13.0499 12.3388Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "top-nav-help",
		},
	],
};

const BasicUsage = () => {
	// These states are kept outside the component because you would most likely want to persist
	// these in something like local storage to work well will page reloads.
	const [selectionId, setSelectionId] = useState<string | null>(
		"dataprivacy-personal-data-types-phone-number"
	);
	const [viewState, setViewState] = useState<ViewState>();
	const [expandedItems, setExpandedItems] = useState<string[]>();

	return (
		<>
			<div style={{ height: "100vh" }}>
				<SideNavigation
					data-observe-key="side-nav"
					mainItems={data.sideNavigation}
					bottomItems={data.sideBottomNavigation}
					selectionId={selectionId}
					setSelectionId={setSelectionId}
					viewState={viewState}
					setViewState={setViewState}
					expandedItems={expandedItems}
					setExpandedItems={setExpandedItems}
					searchHotkey="M"
				/>
			</div>
		</>
	);
};

const FallbackToDefaultExpansion = () => {
	// These states are kept outside the component because you would most likely want to persist
	// these in something like local storage to work well will page reloads.
	const [selectionId, setSelectionId] = useState<string | null>(
		"dataprivacy-personal-data-types-phone-number"
	);
	const [viewState, setViewState] = useState<ViewState>();
	const [expandedItems, setExpandedItems] = useState(["qa", "qa-spelling-v2"]);

	return (
		<>
			<div style={{ height: "100vh" }}>
				<SideNavigation
					data-observe-key="side-nav"
					mainItems={data.sideNavigation}
					bottomItems={data.sideBottomNavigation}
					selectionId={selectionId}
					setSelectionId={setSelectionId}
					viewState={viewState}
					setViewState={setViewState}
					expandedItems={expandedItems}
					setExpandedItems={setExpandedItems}
				/>
			</div>
		</>
	);
};

const LabelUsage = () => {
	// These states are kept outside the component because you would most likely want to persist
	// these in something like local storage to work well will page reloads.
	const [selectionId, setSelectionId] = useState<string | null>(null);
	const [viewState, setViewState] = useState<ViewState>();
	const [expandedItems, setExpandedItems] = useState<string[]>();

	// Copy and wrangle the basic example to show off how to set labels
	const d = JSON.parse(JSON.stringify(data));
	const nav: NavigationItem[] = d.sideNavigation.slice(0, 6);
	nav[0].labels = [{ type: "highlight1", icon: <IconFeatureFlag />, tooltip: "Feature Flag Xyz" }];
	nav[1].labels = [{ type: "highlight3", text: "Internal", tooltip: "Internal use only" }];
	nav[1].children![0].labels = [{ type: "highlight2", text: "Demo", tooltip: "Demo data" }];
	nav[3].labels = [
		{ type: "warning", text: "Beta", tooltip: "Feature is still work in progress" },
		{ type: "highlight1", icon: <IconFeatureFlag />, tooltip: "Feature Flag Xyz" },
	];
	nav[5].labels = [{ type: "highlight2", text: "Demo", tooltip: "Demo data" }];

	return (
		<>
			<div style={{ height: "35rem" }}>
				<SideNavigation
					data-observe-key="side-nav"
					mainItems={nav}
					bottomItems={[]}
					selectionId={selectionId}
					setSelectionId={setSelectionId}
					viewState={viewState}
					setViewState={setViewState}
					expandedItems={expandedItems}
					setExpandedItems={setExpandedItems}
				/>
			</div>
		</>
	);
};

const LinksWithClickUsage = () => {
	// These states are kept outside the component because you would most likely want to persist
	// these in something like local storage to work well will page reloads.
	const [selectionId, setSelectionId] = useState<string | null>(null);
	const [viewState, setViewState] = useState<ViewState>();
	const [expandedItems, setExpandedItems] = useState<string[]>();
	const items = [
		{
			id: "dashboard",
			title: "Dashboard",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M8.94204 10.6454H3.72293C2.80659 10.6454 2.04962 9.88841 2.04962 8.97207V3.75296C2.04962 2.83662 2.80659 2.07965 3.72293 2.07965H8.92212C9.85838 2.07965 10.6153 2.83662 10.6153 3.75296V8.95215C10.6153 9.88841 9.85838 10.6454 8.94204 10.6454Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M8.94204 22H3.72293C2.80659 22 2.04962 21.243 2.04962 20.3267V15.1076C2.04962 14.1912 2.80659 13.4343 3.72293 13.4343H8.92212C9.85838 13.4343 10.5954 14.1912 10.5954 15.1076V20.3068C10.6154 21.243 9.85838 22 8.94204 22Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.2966 13.4343H15.0775C14.1412 13.4343 13.4042 14.1912 13.4042 15.1076V20.3068C13.4042 21.243 14.1612 21.9801 15.0775 21.9801H20.2767C21.2129 21.9801 21.95 21.2231 21.95 20.3068V15.1076C21.9699 14.1912 21.2129 13.4343 20.2966 13.4343Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M20.2966 2H15.0775C14.1612 2 13.4042 2.73705 13.4042 3.67331V8.8725C13.4042 9.80876 14.1612 10.5458 15.0775 10.5458H20.2767C21.2129 10.5458 21.95 9.78884 21.95 8.8725V3.67331C21.9699 2.73705 21.2129 2 20.2966 2Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-dashboard",
			children: [],
			onClick: () => {
				alert("Dashboard clicked");
			},
		},
		{
			id: "dci",
			title: "Digital Certainty Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M11.7357 21.3761C6.56402 21.3761 2.35959 17.1716 2.35959 12C2.35959 6.82834 6.56402 2.6239 11.7357 2.6239C16.9073 2.6239 21.1117 6.82834 21.1117 12C21.1117 17.1716 16.9073 21.3761 11.7357 21.3761ZM11.7357 3.61086C7.11671 3.61086 3.34654 7.38103 3.34654 12C3.34654 16.6189 7.11671 20.3891 11.7357 20.3891C16.3546 20.3891 20.1248 16.6189 20.1248 12C20.1248 7.38103 16.3546 3.61086 11.7357 3.61086Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M11.7357 22.2643C7.47202 22.2643 3.58342 19.5601 2.08324 15.5333C1.82664 14.8227 2.18194 14.0134 2.89255 13.7568C3.60316 13.4804 4.41246 13.8555 4.66907 14.5661C5.77446 17.527 8.61689 19.5009 11.7357 19.5009C15.8809 19.5009 19.2365 16.1452 19.2365 12C19.2365 7.85479 15.8809 4.49913 11.7357 4.49913C10.9658 4.49913 10.3539 3.88722 10.3539 3.1174C10.3539 2.34757 10.9658 1.73566 11.7357 1.73566C17.4008 1.73566 22 6.33487 22 12C22 17.6651 17.4008 22.2643 11.7357 22.2643Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M10.3934 12C10.3934 13.0659 9.60383 13.8752 8.5774 13.8752H7.03775V10.1248H8.5774C9.60383 10.1248 10.3934 10.9341 10.3934 12ZM9.44592 12C9.44592 11.4275 9.07088 11.0525 8.55766 11.0525H8.02471V12.9475H8.55766C9.09062 12.9475 9.44592 12.5724 9.44592 12Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M10.9066 12C10.9066 10.8946 11.7357 10.0458 12.8608 10.0458C13.5517 10.0458 14.1636 10.4011 14.4794 10.934L13.6701 11.4078C13.5319 11.1314 13.2161 10.9735 12.8805 10.9735C12.2686 10.9735 11.8738 11.388 11.8738 12C11.8738 12.5921 12.2686 13.0264 12.8805 13.0264C13.2358 13.0264 13.5319 12.8685 13.6701 12.5921L14.4991 13.0659C14.1636 13.5988 13.5714 13.9541 12.8805 13.9541C11.7357 13.9541 10.9066 13.1053 10.9066 12Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M16.019 10.1248V13.8752H15.0518V10.1248H16.019Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-dci",
			defaultChildId: "dci-overview",
			children: [
				{
					id: "dci-overview",
					title: "DCI overview",
					href: "#dci-overview",
					observeKey: "side-nav-dci-overview",
					children: [],
					onClick: () => {
						alert("DCI overview clicked");
					},
				},
				{
					id: "dci-account-overview",
					title: "My sites",
					href: "#dci-mysites",
					observeKey: "side-nav-dci-account-overview",
					children: [],
					onClick: async () => {
						console.log("DCI My sites clicked");
						return fetch("https://pfg.siteimprove.com/boo").catch((e) =>
							console.log("Response failed", e)
						);
					},
				},
			],
		},
	];

	return (
		<>
			<div style={{ height: "35rem" }}>
				<SideNavigation
					data-observe-key="side-nav"
					mainItems={items}
					bottomItems={[]}
					selectionId={selectionId}
					setSelectionId={setSelectionId}
					viewState={viewState}
					setViewState={setViewState}
					expandedItems={expandedItems}
					setExpandedItems={setExpandedItems}
				/>
			</div>
		</>
	);
};

const dataOneMainNavItem = {
	personFullName: "Siteimprove User",
	languageCulture: "en-US",
	formatCulture: "en-US",
	myProfile: {
		id: "my-profile",
		title: "My profile",
		href: "/Settings/Users/MyProfile",
		icon: null,
		observeKey: "top-nav-my-profile",
	},
	horizontalNavigation: [
		{
			id: "demo-data",
			title: "Configure Demo Data",
			href: "/App/State/ConfigureDemoData/1",
			icon: null,
			observeKey: "top-nav-demo-data",
		},
		{
			id: "signout",
			title: "Sign out",
			href: "/Auth/Logout",
			icon: null,
			observeKey: "top-nav-signout",
		},
	],
	sideNavigation: [
		{
			children: [
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-overview",
					title: "SEO Overview",
					href: "/SEOv2/20063/Overview/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-site-overview",
					title: "My sites",
					href: "/SEOv2/20063/Overview/SiteOverview",
					icon: null,
					observeKey: "side-nav-seo-v2-site-overview",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-insights-feed",
					title: "Insights",
					href: "/SEOv2/20063/Insights/Index?period=Custom",
					icon: null,
					observeKey: "side-nav-seo-v2-insights-feed",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-insights-keyword-monitoring",
					title: "Keyword Monitoring",
					href: "/SEOv2/20063/KeywordMonitoring/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-insights-keyword-monitoring",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-activity-plans",
							title: "Activity Plan overview",
							href: "/SEOv2/20063/ActivityPlansOverview/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-activity-plans",
						},
						{
							children: [],
							defaultChildId: null,
							id: "competitor-analysis",
							title: "Competitor Analysis",
							href: "/SEOv2/20063/CompetitorAnalysis/Analysis",
							icon: null,
							observeKey: "side-nav-competitor-analysis",
						},
					],
					defaultChildId: null,
					id: "seo-v2-activity-plans-overview",
					title: "Activity Plans",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-activity-plans-overview",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-recommendations",
							title: "Issues and recommendations",
							href: "/SEOv2/20063/Recommendations/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-recommendations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-groups",
							title: "Groups",
							href: "/SEOv2/20063/SeoGroups/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-groups",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-content-optimization",
							title: "Content optimization",
							href: "/SEOv2/20063/ContentOptimization/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-content-optimization",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-target-pages",
							title: "Target Pages",
							href: "/SEOv2/20063/TargetPages/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-target-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-publish-tools-snippet-preview",
							title: "Search Snippet Preview",
							href: "/SEOv2/20063/PublishTools/SnippetPreview",
							icon: null,
							observeKey: "side-nav-seo-v2-publish-tools-snippet-preview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-similar-pages",
							title: "Duplicate content",
							href: "/SEOv2/20063/Similarity/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-similar-pages",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-anchor-texts",
							title: "Internal Anchor Texts",
							href: "/SEOv2/20063/Similarity/AnchorTexts",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-anchor-texts",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-optimize-policies",
							title: "SEO Policies",
							href: "/SEOv2/20063/Overview/Policies",
							icon: null,
							observeKey: "side-nav-seo-v2-optimize-policies",
						},
					],
					defaultChildId: null,
					id: "seo-v2-optimize",
					title: "Optimize",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-optimize",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-keyword-discovery",
							title: "Keyword Discovery",
							href: "/SEOv2/20063/KeywordDiscovery/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-keyword-discovery",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-keyword-finder",
							title: "Keyword suggestions",
							href: "/SEOv2/20063/KeywordFinder/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-keyword-finder",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-google-search-console",
							title: "Google Search Console ",
							href: "/SEOv2/20063/ThisYear/SearchConsole/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-google-search-console",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-search-engine-statistics",
							title: "Search Engine Analytics",
							href: "/SEOv2/20063/Custom/SearchEngineStatistics/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-search-engine-statistics",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-organic-search",
							title: "Top-ranking Pages by Keyword",
							href: "/SEOv2/20063/KeywordSearch/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-organic-search",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-organic-keywords-by-domain",
							title: "Top Keywords by Domain",
							href: "/SEOv2/20063/DomainSearch/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-organic-keywords-by-domain",
						},
					],
					defaultChildId: null,
					id: "seo-v2-insights",
					title: "Research",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-insights",
				},
				{
					children: [],
					defaultChildId: null,
					id: "seo-v2-google-my-business",
					title: "Google My Business",
					href: "/SEOv2/LocalSeo?period=Custom",
					icon: null,
					observeKey: "side-nav-seo-v2-google-my-business",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-overview",
							title: "Backlinks Overview",
							href: "/SEOv2/20063/BacklinksOverview/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-overview",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-link-profile",
							title: "Link Profile",
							href: "/SEOv2/20063/BacklinksLinkProfile/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-link-profile",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-top-back-links",
							title: "High-value Backlinks",
							href: "/SEOv2/20063/BacklinksTopBacklinks/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-top-back-links",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-high-risk-backlinks",
							title: "Low-value Backlinks",
							href: "/SEOv2/20063/BacklinksHighRiskBacklinks/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-high-risk-backlinks",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-backlinks-link-reclamation",
							title: "Link Reclamation",
							href: "/SEOv2/20063/BacklinksLinkReclamation/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-backlinks-link-reclamation",
						},
					],
					defaultChildId: null,
					id: "seo-v2-backlinks",
					title: "Backlinks",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-backlinks",
				},
				{
					children: [
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-integrations",
							title: "Integrations",
							href: "/SEOv2/20063/SeoSettings/Integrations",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-integrations",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-keyword-import",
							title: "Bulk Keyword Import",
							href: "/SEOv2/20063/Import/Keywords",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-keyword-import",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-insights-google-search-console-settings",
							title: "Google Search Console settings",
							href: "/SEOv2/SearchConsoleSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-insights-google-search-console-settings",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-local-seo",
							title: "Local SEO settings",
							href: "/SEOv2/LocalSeoSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-local-seo",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-competitor",
							title: "Competitor Settings",
							href: "/SEOv2/20063/CompetitorSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-competitor",
						},
						{
							children: [],
							defaultChildId: null,
							id: "seo-v2-settings-keyword",
							title: "Keyword Settings",
							href: "/SEOv2/KeywordSettings/Index",
							icon: null,
							observeKey: "side-nav-seo-v2-settings-keyword",
						},
					],
					defaultChildId: null,
					id: "seo-v2-settings",
					title: "SEO Settings",
					href: "/SEOv2/Home/Index",
					icon: null,
					observeKey: "side-nav-seo-v2-settings",
				},
			],
			defaultChildId: "seo-v2-overview",
			id: "seo-v2",
			title: "SEO Enterprise",
			href: "/SEOv2/Home/Index",
			icon: '\r\n\t\t\t\t\t<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">\r\n\t\t\t\t\t\t<path d="M21.6424 19.9372L17.962 16.2568C20.6023 12.7763 20.3423 7.77571 17.1619 4.59532C13.7014 1.13489 8.06075 1.13489 4.60032 4.59532C2.92011 6.27553 2 8.5158 2 10.8761C2 11.2961 2.02 11.6962 2.08001 12.0962C2.08001 12.0962 3.68021 11.7162 4.4003 10.7561C4.4403 9.07587 5.10038 7.49568 6.30053 6.29553C7.56068 5.03537 9.22089 4.39529 10.8811 4.39529C12.5413 4.39529 14.2015 5.03537 15.4617 6.29553C18.002 8.81584 18.002 12.9363 15.4817 15.4767C14.6416 16.3168 13.6214 16.8768 12.5413 17.1569L11.9012 17.4169C11.3211 17.6569 10.8611 18.117 10.6211 18.6971L10.181 19.7372C10.421 19.7572 10.6411 19.7572 10.8811 19.7572C12.7813 19.7572 14.6816 19.1571 16.2618 17.957L19.9422 21.6374C20.1822 21.8774 20.4823 21.9975 20.7823 21.9975C21.0824 21.9975 21.4024 21.8774 21.6224 21.6374C22.1225 21.1774 22.1225 20.4173 21.6424 19.9372Z" fill="white"/>\r\n\t\t\t\t\t\t<path d="M12.9827 14.1365C13.2828 14.2565 13.2628 14.6766 12.9827 14.7966L11.1423 15.5767C10.0821 16.0167 9.24196 16.8568 8.80187 17.917C8.54182 18.557 8.24176 19.2571 8.02172 19.7572C7.9017 20.0572 7.48161 20.0572 7.36159 19.7572C7.16155 19.2771 6.8815 18.637 6.64145 18.037C6.16135 16.8968 5.24117 15.9767 4.10095 15.4967L2.38061 14.7966C2.08055 14.6766 2.08055 14.2565 2.38061 14.1365L4.10095 13.4164C5.24117 12.9363 6.16135 12.0162 6.64145 10.8761L7.36159 9.15587C7.48161 8.85584 7.9017 8.85584 8.02172 9.15587L8.80187 10.9961C9.24196 12.0562 10.0821 12.8963 11.1423 13.3364C11.7625 13.6364 12.4626 13.9165 12.9827 14.1365Z" fill="white"/>\r\n\t\t\t\t\t</svg>\r\n\t\t\t\t',
			observeKey: "side-nav-seo-v2",
		},
	],
};

const OneMainNavItemFixedViewUsage = () => {
	// These states are kept outside the component because you would most likely want to persist
	// these in something like local storage to work well will page reloads.
	const [selectionId, setSelectionId] = useState<string | null>(null);
	const [viewState, setViewState] = useState<ViewState>("fixed");
	const [expandedItems, setExpandedItems] = useState<string[]>();

	return (
		<>
			<div style={{ height: "75vh" }}>
				<SideNavigation
					data-observe-key="side-nav"
					mainItems={dataOneMainNavItem.sideNavigation}
					bottomItems={[]}
					selectionId={selectionId}
					setSelectionId={setSelectionId}
					viewState={viewState}
					setViewState={setViewState}
					expandedItems={expandedItems}
					setExpandedItems={setExpandedItems}
					backButtonLabel="Back to Quality Assurance"
					backButtonUrl="http://www.siteimprove.com"
				/>
			</div>
		</>
	);
};
